import React, { useState, useEffect, useCallback, useRef } from 'react';
import GoalGuardrailModal from '../components/GoalGuardrailModal';
import RaceTypePickerModal from '../components/RaceTypePickerModal';
import LoadBalanceModal from '../components/LoadBalanceModal';
import { planRegenRequest } from '../utils/planRegenRequest';
import { validateUserGoal, validateKmDaysMatrix } from '../utils/goalValidator';
import { RUNNING_LEVEL_OPTIONS, CYCLING_LEVEL_OPTIONS, getLevelLabel } from '../utils/levels';
import { showToast } from '../utils/toast';
import { planRebuildChange, runPlanRebuild, REBUILD_WARNING, REBUILD_CONFIRM_LABEL } from '../utils/planRebuild';
import { CYCLING_ENABLED } from '../config/features';
import authFetch from '../utils/authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// 📅 Enhanced Calendar Component with navigation and workouts
const EnhancedCalendar = ({ events = [], workouts = [] }) => {
  const [viewDate, setViewDate] = useState(new Date());
  
  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();
  const today = new Date();
  
  const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
  const dayNames = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
  
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  
  // Navigate months
  const prevMonth = () => setViewDate(new Date(currentYear, currentMonth - 1, 1));
  const nextMonth = () => setViewDate(new Date(currentYear, currentMonth + 1, 1));
  const goToToday = () => setViewDate(new Date());
  
  // Get events for this month (races)
  const monthEvents = events.filter(e => {
    const eventDate = new Date(e.date || e.race_date);
    return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
  });
  
  // Create map of day -> events for quick lookup
  const eventsByDay = {};
  monthEvents.forEach(e => {
    const day = new Date(e.date || e.race_date).getDate();
    if (!eventsByDay[day]) eventsByDay[day] = [];
    eventsByDay[day].push(e);
  });
  
  // Get workouts for this month (from plans)
  const workoutsByDay = {};
  workouts.forEach(w => {
    if (w.date) {
      const wDate = new Date(w.date);
      if (wDate.getMonth() === currentMonth && wDate.getFullYear() === currentYear) {
        const day = wDate.getDate();
        if (!workoutsByDay[day]) workoutsByDay[day] = [];
        workoutsByDay[day].push(w);
      }
    }
  });
  
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);
  
  const isToday = (day) => day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
  
  // Build tooltip for a day
  const getTooltip = (day) => {
    if (!day) return '';
    const parts = [];
    
    // Add races
    eventsByDay[day]?.forEach(e => {
      const icon = e.is_goal_race ? '⭐' : e.sport_type === 'cycling' ? '🚴' : '🏃';
      parts.push(`${icon} תחרות: ${e.name || e.race_name}`);
    });
    
    // Add workouts (skip rest days)
    workoutsByDay[day]?.forEach(w => {
      if (!w.isRest) {
        const icon = w.sport_type === 'cycling' ? '🚴' : '🏃';
        const dist = w.distance ? ` (${w.distance})` : '';
        parts.push(`${icon} ${w.title}${dist}`);
      }
    });
    
    return parts.join('\n');
  };
  
  return (
    <div className="p-4 rounded-xl bg-[#1e293b] border border-white/10">
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={prevMonth}
          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white flex items-center justify-center transition-colors"
        >
          →
        </button>
        <div className="text-center">
          <h4 className="text-white font-bold text-lg">📅 {monthNames[currentMonth]} {currentYear}</h4>
          {(currentMonth !== today.getMonth() || currentYear !== today.getFullYear()) && (
            <button onClick={goToToday} className="text-xs text-blue-400 hover:text-blue-300">
              חזור להיום
            </button>
          )}
        </div>
        <button 
          onClick={nextMonth}
          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white flex items-center justify-center transition-colors"
        >
          ←
        </button>
      </div>
      
      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {dayNames.map(d => <div key={d} className="text-gray-500 text-sm font-medium py-1">{d}</div>)}
      </div>
      
      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {days.map((day, i) => {
          const hasRunningRace = eventsByDay[day]?.some(e => (e.sport_type || 'running') === 'running');
          const hasCyclingRace = eventsByDay[day]?.some(e => e.sport_type === 'cycling');
          const hasRunningWorkout = workoutsByDay[day]?.some(w => w.sport_type === 'running');
          const hasCyclingWorkout = workoutsByDay[day]?.some(w => w.sport_type === 'cycling');
          const hasGoalRace = eventsByDay[day]?.some(e => e.is_goal_race);
          const hasAnyWorkout = workoutsByDay[day]?.length > 0;
          
          const tooltip = getTooltip(day);
          
          return (
            <div 
              key={i} 
              className={`relative py-2 px-1 rounded-lg text-sm transition-all cursor-default ${
                isToday(day) ? 'bg-purple-600 text-white font-bold ring-2 ring-purple-400' :
                hasGoalRace ? 'bg-yellow-500/30 text-yellow-300 font-bold' :
                hasRunningRace || hasCyclingRace ? 'bg-red-500/20 text-red-200 font-semibold' :
                hasAnyWorkout ? 'bg-white/5' :
                day ? 'text-gray-400 hover:bg-white/5' : ''
              }`}
              title={tooltip}
            >
              {day || ''}
              {/* Indicators */}
              {day && (hasRunningRace || hasCyclingRace || hasRunningWorkout || hasCyclingWorkout) && (
                <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {hasRunningRace && <span className="w-2.5 h-2.5 rounded-full bg-red-500 ring-1 ring-red-400"></span>}
                  {hasCyclingRace && <span className="w-2.5 h-2.5 rounded-full bg-pink-500 ring-1 ring-pink-400"></span>}
                  {hasRunningWorkout && !hasRunningRace && <span className="w-1.5 h-1.5 rounded-full bg-blue-400/60"></span>}
                  {hasCyclingWorkout && !hasCyclingRace && <span className="w-1.5 h-1.5 rounded-full bg-orange-400/60"></span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
          <span className="text-gray-400">מרוץ ריצה</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-pink-500"></span>
          <span className="text-gray-400">תחרות רכיבה</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400/60"></span>
          <span className="text-gray-400">אימון ריצה</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-400/60"></span>
          <span className="text-gray-400">אימון רכיבה</span>
        </div>
      </div>
      
      {/* Events this month */}
      {monthEvents.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/10 space-y-2">
          <p className="text-gray-400 text-xs font-medium">🏁 תחרויות בחודש זה:</p>
          {monthEvents.map((e, i) => {
            const isCycling = e.sport_type === 'cycling';
            const eventDay = new Date(e.date || e.race_date).getDate();
            return (
              <div key={i} className={`flex items-center gap-2 p-2 rounded-lg ${
                e.is_goal_race ? 'bg-yellow-500/20' : isCycling ? 'bg-pink-500/15' : 'bg-red-500/15'
              }`}>
                <span className="text-lg">{e.is_goal_race ? '⭐' : isCycling ? '🚴' : '🏃'}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    e.is_goal_race ? 'text-yellow-300' : isCycling ? 'text-pink-300' : 'text-red-300'
                  }`}>
                    {e.name || e.race_name}
                  </p>
                  <p className="text-xs text-gray-500">{eventDay}/{currentMonth + 1}/{currentYear}</p>
                </div>
                {e.is_goal_race && (
                  <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">יעד</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {monthEvents.length === 0 && (
        <div className="mt-4 pt-3 border-t border-white/10 text-center">
          <p className="text-gray-500 text-sm">אין תחרויות בחודש זה</p>
        </div>
      )}
    </div>
  );
};

function CoachDashboard({ userId, userEmail, onBack }) {
  const [isLoading, setIsLoading] = useState(true);
  const [coachProfile, setCoachProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [loadError, setLoadError] = useState(null);
  
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerForm, setRegisterForm] = useState({ name: '', phone: '', plan: 'solo' });

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/register?user_id=${userId}&name=${encodeURIComponent(registerForm.name)}&email=${encodeURIComponent(userEmail)}&phone=${encodeURIComponent(registerForm.phone || '')}&plan_type=${registerForm.plan}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        showToast(`🎉 ברוך הבא! נרשמת בהצלחה כמאמן.\n\nחבילה: ${registerForm.plan}\nמקסימום מתאמנים: ${data.max_athletes}`);
        setShowRegisterForm(false);
        loadCoachData();
      } else { showToast(data.detail || 'שגיאה ברישום', 'error'); }
    } catch (error) { console.error('Registration error:', error); showToast('שגיאה ברישום, נסה שוב', 'error'); }
    finally { setRegisterLoading(false); }
  };

  useEffect(() => { loadCoachData(); }, [userId]);

  const loadCoachData = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const profileRes = await authFetch(`${API_BASE_URL}/coach/profile?user_id=${userId}`);
      const profileData = await profileRes.json();
      if (!profileData.is_coach) { setCoachProfile(null); setIsLoading(false); return; }
      setCoachProfile(profileData);
      try {
        const dashboardRes = await authFetch(`${API_BASE_URL}/coach/dashboard?user_id=${userId}`);
        if (dashboardRes.ok) { const dashboardData = await dashboardRes.json(); setDashboard(dashboardData); }
      } catch (dashErr) { console.log('Dashboard fetch failed:', dashErr); }
      try {
        const athletesRes = await authFetch(`${API_BASE_URL}/coach/athletes?user_id=${userId}`);
        if (athletesRes.ok) { const athletesData = await athletesRes.json(); setAthletes(athletesData.athletes || []); }
      } catch (athErr) { console.log('Athletes fetch failed:', athErr); }
    } catch (error) { console.error('Error loading coach data:', error); setLoadError('שגיאה בטעינת נתונים'); }
    finally { setIsLoading(false); }
  };

  const createInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/invite?user_id=${userId}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { setInviteLink(data.invite_link); setShowInviteModal(true); }
      else { showToast(data.detail || 'שגיאה ביצירת הזמנה', 'error'); }
    } catch (error) { console.error('Error creating invite:', error); showToast('שגיאה ביצירת הזמנה', 'error'); }
    finally { setInviteLoading(false); }
  };

  const copyInviteLink = () => { navigator.clipboard.writeText(inviteLink); showToast('הקישור הועתק!'); };

  const removeAthlete = async (athleteId, athleteName) => {
    if (!window.confirm(`האם להסיר את ${athleteName} מהקבוצה?`)) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athleteId}?user_id=${userId}`, { method: 'DELETE' });
      if (res.ok) { setAthletes(athletes.filter(a => a.id !== athleteId)); showToast('המתאמן הוסר בהצלחה'); }
    } catch (error) { console.error('Error removing athlete:', error); }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg mb-4 animate-pulse"><span className="text-3xl">🏋️</span></div>
          <p className="text-gray-400">טוען דשבורד מאמן...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-500/20 flex items-center justify-center shadow-lg mb-4"><span className="text-3xl">❌</span></div>
          <p className="text-red-400 mb-4">{loadError}</p>
          <button onClick={loadCoachData} className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500">נסה שוב</button>
        </div>
      </div>
    );
  }

  if (!coachProfile) {
    // Coach registration is currently disabled - show "coming soon" message
    return (
      <div className="min-h-screen bg-[#0f172a]" dir="rtl">
        <header className="sticky top-0 z-20 bg-[#0f172a]/90 backdrop-blur-lg border-b border-white/5">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <h1 className="text-lg font-bold text-white">אזור מאמנים</h1>
            <button onClick={onBack} className="text-gray-400 hover:text-white">🏠</button>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-6"><span className="text-5xl">🔒</span></div>
            <h2 className="text-2xl font-bold text-white mb-4">אזור מאמנים - בקרוב</h2>
            <p className="text-gray-400 mb-6">הרשמה למאמנים סגורה כרגע.</p>
            <div className="bg-[#1e293b] rounded-2xl p-6 border border-white/5 text-right mb-6">
              <h3 className="text-white font-semibold mb-4">מעוניין להצטרף כמאמן?</h3>
              <p className="text-gray-400 mb-4">צור קשר איתנו ונחזור אליך בהקדם:</p>
              <a href="mailto:liorpar@gmail.com" className="text-purple-400 hover:text-purple-300 font-medium">liorpar@gmail.com</a>
            </div>
            <button onClick={onBack} className="w-full py-4 rounded-xl bg-gradient-to-r from-gray-600 to-gray-500 text-white font-bold text-lg hover:opacity-90 transition">חזור למסך הראשי</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a]" dir="rtl">
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[128px] pointer-events-none"></div>
      <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-pink-600/10 rounded-full blur-[128px] pointer-events-none"></div>
      <header className="sticky top-0 z-20 bg-[#0f172a]/90 backdrop-blur-lg border-b border-white/5">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"><span className="text-lg">🏋️</span></div>
              <div><h1 className="text-lg font-bold text-white">דשבורד מאמן</h1><p className="text-xs text-gray-500">{coachProfile.name}</p></div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium">{coachProfile.plan_type}</span>
              <button onClick={onBack} className="w-10 h-10 rounded-xl bg-[#1e293b] border border-white/10 flex items-center justify-center text-gray-400 hover:text-white">🏠</button>
            </div>
          </div>
        </div>
      </header>
      <div className="border-b border-white/5">
        <div className="container mx-auto px-4">
          <div className="flex gap-1">
            {[{ id: 'overview', label: 'סקירה', icon: '📊' },{ id: 'athletes', label: 'מתאמנים', icon: '👥' },{ id: 'settings', label: 'הגדרות', icon: '⚙️' }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition ${activeTab === tab.id ? 'text-purple-400 border-b-2 border-purple-400' : 'text-gray-500 hover:text-gray-300'}`}><span className="ml-1">{tab.icon}</span>{tab.label}</button>
            ))}
          </div>
        </div>
      </div>
      <main className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="rounded-xl bg-[#1e293b] border border-white/5 p-4 text-center"><p className="text-3xl font-bold text-purple-400">{dashboard?.stats?.total_athletes || athletes.length || 0}</p><p className="text-xs text-gray-500 mt-1">מתאמנים</p></div>
                <div className="rounded-xl bg-[#1e293b] border border-white/5 p-4 text-center"><p className="text-3xl font-bold text-white">{dashboard?.stats?.total_km_this_week || 0}</p><p className="text-xs text-gray-500 mt-1">ק"מ השבוע</p></div>
                <div className="rounded-xl bg-[#1e293b] border border-white/5 p-4 text-center"><p className="text-3xl font-bold text-yellow-400">{dashboard?.stats?.alerts_count || 0}</p><p className="text-xs text-gray-500 mt-1">התראות</p></div>
                <div className="rounded-xl bg-[#1e293b] border border-white/5 p-4 text-center"><p className="text-3xl font-bold text-cyan-400">{dashboard?.stats?.upcoming_races_count || 0}</p><p className="text-xs text-gray-500 mt-1">תחרויות קרובות</p></div>
              </div>
              <div className="mb-6 p-4 rounded-xl bg-[#1e293b] border border-white/5">
                <div className="flex items-center justify-between mb-2"><span className="text-gray-400 text-sm">תפוסה</span><span className="text-white text-sm font-medium">{coachProfile?.current_athletes || athletes.length || 0}/{coachProfile?.max_athletes || 10} מתאמנים</span></div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all" style={{ width: `${((coachProfile?.current_athletes || athletes.length || 0) / (coachProfile?.max_athletes || 10)) * 100}%` }}></div></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button onClick={createInvite} disabled={inviteLoading || (coachProfile && coachProfile.available_slots === 0)} className="p-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 text-right hover:border-purple-500/50 transition disabled:opacity-50"><span className="text-2xl">➕</span><p className="text-white font-medium mt-2">הזמן מתאמן</p><p className="text-gray-500 text-xs">צור קישור הזמנה</p></button>
                <button onClick={() => setActiveTab('athletes')} className="p-4 rounded-xl bg-[#1e293b] border border-white/5 text-right hover:border-white/20 transition"><span className="text-2xl">👥</span><p className="text-white font-medium mt-2">צפה במתאמנים</p><p className="text-gray-500 text-xs">{athletes.length} מתאמנים פעילים</p></button>
              </div>
              {(!athletes || athletes.length === 0) && (
                <div className="p-6 rounded-xl bg-gradient-to-r from-purple-600/10 to-pink-600/10 border border-purple-500/20 text-center">
                  <span className="text-4xl mb-3 block">🎉</span><h3 className="text-white font-bold text-lg mb-2">ברוך הבא!</h3><p className="text-gray-400 text-sm mb-4">התחל להוסיף מתאמנים לקבוצה שלך</p>
                  <button onClick={createInvite} disabled={inviteLoading} className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium hover:opacity-90 transition">{inviteLoading ? '⏳' : '➕'} הזמן את המתאמן הראשון</button>
                </div>
              )}
            </>
          )}
          {activeTab === 'athletes' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">המתאמנים שלי</h2>
                <button onClick={createInvite} disabled={inviteLoading || (coachProfile && coachProfile.available_slots === 0)} className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 transition disabled:opacity-50">{inviteLoading ? '...' : '➕ הזמן מתאמן'}</button>
              </div>
              {athletes.length === 0 ? (
                <div className="text-center py-12"><span className="text-5xl mb-4 block">👥</span><p className="text-gray-400">עדיין אין מתאמנים</p></div>
              ) : (
                <div className="space-y-3">
                  {athletes.map((athlete) => (
                    <div key={athlete.id} className="p-4 rounded-xl bg-[#1e293b] border border-white/5 hover:border-purple-500/30 transition">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center"><span className="text-xl">👤</span></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-medium truncate">{athlete.name}</h3>
                          </div>
                          <p className="text-gray-500 text-sm truncate">{athlete.email || 'אין אימייל'}</p>
                        </div>
                        <div className="text-left hidden sm:block">
                          {athlete.profile && (<><p className="text-white text-sm">{athlete.profile.avg_weekly_km || '—'} ק"מ/שבוע</p><p className="text-gray-500 text-xs">{athlete.profile.level || 'לא צוין'}</p></>)}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setSelectedAthlete(athlete)} className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition flex items-center justify-center" title="צפה בפרטים">👁️</button>
                          <button onClick={() => removeAthlete(athlete.id, athlete.name)} className="w-8 h-8 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition flex items-center justify-center" title="הסר מהקבוצה">✕</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white">הגדרות</h2>
              <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5">
                <h3 className="text-white font-medium mb-3">החבילה שלי</h3>
                <div className="flex items-center justify-between">
                  <div><p className="text-purple-400 font-bold text-lg capitalize">{coachProfile.plan_type}</p><p className="text-gray-500 text-sm">עד {coachProfile.max_athletes} מתאמנים</p></div>
                  <button className="px-4 py-2 rounded-xl bg-purple-500/20 text-purple-400 text-sm hover:bg-purple-500/30 transition">שדרג חבילה</button>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5">
                <h3 className="text-white font-medium mb-3">פרטי מאמן</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">שם:</span><span className="text-white">{coachProfile.name}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">אימייל:</span><span className="text-white">{coachProfile.email}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1e293b] rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">🔗 הזמן מתאמן חדש</h3>
            <p className="text-gray-400 text-sm mb-4">שלח את הקישור הזה למתאמן</p>
            <div className="p-3 rounded-xl bg-[#0f172a] border border-white/10 mb-4"><p className="text-cyan-400 text-sm break-all font-mono">{inviteLink}</p></div>
            <div className="flex gap-3">
              <button onClick={copyInviteLink} className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-500 transition">📋 העתק קישור</button>
              <button onClick={() => setShowInviteModal(false)} className="px-4 py-3 rounded-xl bg-gray-700 text-white hover:bg-gray-600 transition">סגור</button>
            </div>
          </div>
        </div>
      )}
      {selectedAthlete && <AthleteDetailView athlete={selectedAthlete} coachUserId={userId} onClose={() => setSelectedAthlete(null)} />}
    </div>
  );
}

function AthleteDetailView({ athlete, coachUserId, onClose }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [activities, setActivities] = useState([]);
  const [activitiesFilter, setActivitiesFilter] = useState('all');  // all/running/cycling
  const [athleteData, setAthleteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [activitySplits, setActivitySplits] = useState({});
  const [loadingSplits, setLoadingSplits] = useState(null);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [coachNotes, setCoachNotes] = useState(athlete.notes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  
  const [existingPlans, setExistingPlans] = useState({ 
    running: { current: null, next: null },
    cycling: { current: null, next: null }
  });
  const [planSportTab, setPlanSportTab] = useState('running');
  
  // 🚴🏃 NEW: Sport type tab for races
  const [racesSportTab, setRacesSportTab] = useState('running');
  
  // 📅 Extract workouts from plans for calendar display
  const getWorkoutsFromPlans = () => {
    const workouts = [];

    const extractFromPlan = (plan, sportType) => {
      if (!plan?.week_start_date) return;

      const weekStart = new Date(plan.week_start_date);
      let planText = '';
      let v2Days = null;

      // Get plan text from workouts_json. Newer V2 plans have a
      // structured `days` array — prefer it over the legacy plan_text.
      // 2026-06-11 fix: calendar was empty because V2 plans don't carry
      // plan_text, only days[].
      if (plan.workouts_json) {
        const data = typeof plan.workouts_json === 'string'
          ? JSON.parse(plan.workouts_json)
          : plan.workouts_json;
        planText = data.plan_text || '';
        if (Array.isArray(data.days)) v2Days = data.days;
      }

      // V2 path: extract directly from structured days[].
      if (v2Days && v2Days.length) {
        for (const day of v2Days) {
          if (!day || day.is_rest) continue;
          // Real workout? type must be present; cycling/running ride.
          if (!day.type) continue;
          const km = day.km ? `${day.km} ק"מ` : (day.duration_min ? `${day.duration_min} דק'` : '');
          workouts.push({
            date: day.date || '',
            title: day.type,
            distance: km,
            sport_type: sportType,
            isRest: false,
          });
        }
        return;
      }

      if (!planText) return;

      // Hebrew day names indexed by JavaScript getDay() (0=Sunday, 1=Monday, etc.)
      const dayNamesByJsDay = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

      // Create 7 days starting from week_start_date
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const workoutDate = new Date(weekStart);
        workoutDate.setDate(weekStart.getDate() + dayIndex);
        const dateStr = workoutDate.toISOString().split('T')[0];

        // Get actual day of week (0=Sunday, 6=Saturday)
        const actualDayOfWeek = workoutDate.getDay();
        const dayName = dayNamesByJsDay[actualDayOfWeek];

        // Find content for this specific day - try multiple patterns
        const dayPatterns = [
          // Pattern 1: ### יום ראשון | 22.02.2026 (markdown header with pipe) - RUNNING FORMAT
          new RegExp(`###\\s*(?:יום\\s*)?${dayName}\\s*\\|[^\\n]*\\n([\\s\\S]*?)(?=###|$)`, 'i'),
          // Pattern 2: **יום שני...** or **שני...**
          new RegExp(`\\*\\*(?:יום\\s*)?${dayName}[^*]*\\*\\*([^#]*?)(?=\\*\\*|##|$)`, 'is'),
          // Pattern 3: שני, 6.2.2026...
          new RegExp(`${dayName}[,\\s-]+\\d+\\.\\d+\\.\\d+([^#]*?)(?=##|ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת|$)`, 'is'),
          // Pattern 4: יום שני - or שני -
          new RegExp(`(?:יום\\s*)?${dayName}\\s*[-:]([^#]*?)(?=יום\\s*(?:ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)|##|$)`, 'is'),
          // Pattern 5: Just the day name followed by content (simpler)
          new RegExp(`${dayName}[:\\s]+([^]*?)(?=(?:ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)[:\\s]|##|$)`, 'i')
        ];

        let dayMatch = null;
        for (const pattern of dayPatterns) {
          dayMatch = planText.match(pattern);
          if (dayMatch) break;
        }

        if (dayMatch) {
          const dayContent = dayMatch[1] || dayMatch[0];

          // Check if it's a rest day
          const isRest = /מנוחה|יום מנוחה|Rest|חופש|ללא אימון/i.test(dayContent);

          // Extract workout type - must match a specific type to count as workout
          let workoutType = null;
          let hasSpecificWorkout = false;

          if (/ריצה ארוכה|Long Run/i.test(dayContent)) { workoutType = 'ריצה ארוכה'; hasSpecificWorkout = true; }
          else if (/אינטרוול|Interval|מהירות/i.test(dayContent)) { workoutType = 'אינטרוולים'; hasSpecificWorkout = true; }
          else if (/טמפו|Tempo|קצב/i.test(dayContent)) { workoutType = 'טמפו'; hasSpecificWorkout = true; }
          else if (/ריצה קלה|Easy|התאוששות|קלה|נמוכה/i.test(dayContent)) { workoutType = 'ריצה קלה'; hasSpecificWorkout = true; }
          else if (/פארטלק|Fartlek/i.test(dayContent)) { workoutType = 'פארטלק'; hasSpecificWorkout = true; }
          else if (/גבעות|Hills|עליות/i.test(dayContent)) { workoutType = 'גבעות'; hasSpecificWorkout = true; }
          else if (/רכיבה ארוכה/i.test(dayContent)) { workoutType = 'רכיבה ארוכה'; hasSpecificWorkout = true; }
          else if (/רכיבה קלה/i.test(dayContent)) { workoutType = 'רכיבה קלה'; hasSpecificWorkout = true; }

          // Extract distance if available - also counts as having a workout
          const distanceMatch = dayContent.match(/(\d+(?:\.\d+)?)\s*ק"מ|(\d+(?:\.\d+)?)\s*km/i);
          const distance = distanceMatch ? (distanceMatch[1] || distanceMatch[2]) + ' ק"מ' : '';

          // Also check for duration (indicates workout)
          const hasDuration = /\d+\s*דק|דקות|\d+\s*שע|שעות|\d+:\d+/i.test(dayContent);

          // If has distance or duration, it's a workout even without specific type keyword
          if (!hasSpecificWorkout && (distanceMatch || hasDuration)) {
            workoutType = sportType === 'cycling' ? 'רכיבה' : 'ריצה';
            hasSpecificWorkout = true;
          }

          // Only add if it's NOT a rest day AND has actual workout content
          if (!isRest && hasSpecificWorkout) {
            workouts.push({
              date: dateStr,
              title: workoutType || (sportType === 'cycling' ? 'רכיבה' : 'ריצה'),
              distance: distance,
              sport_type: sportType,
              isRest: false
            });
          }
        }
      }
    };

    // Extract from all plans
    extractFromPlan(existingPlans.running.current, 'running');
    extractFromPlan(existingPlans.running.next, 'running');
    extractFromPlan(existingPlans.cycling.current, 'cycling');
    extractFromPlan(existingPlans.cycling.next, 'cycling');

    return workouts;
  };
  
  const calendarWorkouts = getWorkoutsFromPlans();
  
  const [editProfileData, setEditProfileData] = useState(null);
  const coachProfileBaseRef = useRef(null);   // the athlete's saved plan settings (2026-10-30 rebuild warning)
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  // Open state for the LoadBalanceModal — set when the user clicks
  // Save with a km × days combo that violates the matrix. Cleared
  // when they pick "תקן" or "שמור בכל זאת".
  const [loadBalanceModal, setLoadBalanceModal] = useState({ open: false, requiredDays: 0 });
  const [showAddRace, setShowAddRace] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  const [raceForm, setRaceForm] = useState({ race_name: '', race_date: '', distance: '', target_time: '', is_goal_race: false, sport_type: 'running' });
  const [isSavingRace, setIsSavingRace] = useState(false);
  const [raceGuardrail, setRaceGuardrail] = useState(null); // {result} | null
  const [coachRaceTypePicker, setCoachRaceTypePicker] = useState(false);
  
  const [editingPlanType, setEditingPlanType] = useState(null);
  const [editedPlanText, setEditedPlanText] = useState('');
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(true);
  
  const [allEvents, setAllEvents] = useState([]);
  const [cyclingProfile, setCyclingProfile] = useState(null);
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

  // Fetch splits for a specific activity on-demand
  const fetchSplitsForActivity = async (activityId) => {
    if (activitySplits[activityId]) return; // Already fetched

    setLoadingSplits(activityId);
    try {
      // Bearer token: the backend allows this only for a coach with an ACTIVE link to the athlete.
      const response = await authFetch(`${API_BASE_URL}/activities/${activityId}/splits?user_id=${athlete.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setActivitySplits(prev => ({
          ...prev,
          [activityId]: data.splits || []
        }));
      }
    } catch (err) {
      console.error('Error fetching splits:', err);
    } finally {
      setLoadingSplits(null);
    }
  };

  const loadChatHistory = async () => {
    setIsChatHistoryLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/chat-history?coach_user_id=${coachUserId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          // Defensive: drop empty rows so they don't render as blank bubbles
          const cleaned = data.messages
            .filter(m => m && m.text && String(m.text).trim().length > 0)
            .map(m => ({ role: m.role, text: m.text, time: new Date(m.time) }));
          setAiChatMessages(cleaned);
        }
      }
    } catch (error) { console.error('Error loading chat history:', error); }
    finally { setIsChatHistoryLoading(false); }
  };

  const clearChatHistory = async () => {
    if (!window.confirm('האם למחוק את כל היסטוריית השיחה?')) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/chat-history?coach_user_id=${coachUserId}`, { method: 'DELETE' });
      if (res.ok) { setAiChatMessages([]); showToast('ההיסטוריה נמחקה'); }
    } catch (error) { console.error('Error clearing chat:', error); }
  };

  useEffect(() => { loadAthleteData(); loadMessages(); loadExistingPlans(); loadChatHistory(); }, [athlete.user_id]);

  const loadAthleteData = async () => {
    setIsLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/activities?coach_user_id=${coachUserId}`);
      if (res.ok) { 
        const data = await res.json(); 
        setAthleteData(data); 
        setActivities(data.activities || []);
        
        // Load races from backend
        let allRaces = (data.all_races || []).map(r => ({ ...r, sport_type: r.sport_type || 'running' }));
        
        // Helper function to check if race already exists (fuzzy match by name)
        const raceExists = (name) => {
          if (!name) return true;
          const normalizedName = name.trim().toLowerCase();
          return allRaces.some(r => {
            const existingName = (r.name || r.race_name || '').trim().toLowerCase();
            return existingName === normalizedName;
          });
        };
        
        // 🏃 Add running goal race from profile if not already in races
        if (data.profile?.goal_race && data.profile?.goal_date) {
          if (!raceExists(data.profile.goal_race)) {
            allRaces.push({
              id: 'running_goal_from_profile',
              name: data.profile.goal_race,
              race_name: data.profile.goal_race,
              date: data.profile.goal_date,
              race_date: data.profile.goal_date,
              distance: data.profile.goal_distance || '',
              target_time: data.profile.goal_time || '',
              is_goal_race: true,
              sport_type: 'running',
              from_profile: true
            });
          }
        }
        
        // 🚴 Fetch cycling profile and add goal event if not already in races
        try {
          const cyclingRes = await fetch(`${API_BASE_URL}/profile/cycling/${athlete.user_id}`);
          const cyclingData = await cyclingRes.json();
          if (cyclingData && !cyclingData.error) {
            setCyclingProfile(cyclingData);
            
            if (cyclingData.cycling_goal_event && cyclingData.cycling_goal_date) {
              if (!raceExists(cyclingData.cycling_goal_event)) {
                allRaces.push({
                  id: 'cycling_goal_from_profile',
                  name: cyclingData.cycling_goal_event,
                  race_name: cyclingData.cycling_goal_event,
                  date: cyclingData.cycling_goal_date,
                  race_date: cyclingData.cycling_goal_date,
                  distance: cyclingData.cycling_goal_distance || '',
                  target_time: '',
                  is_goal_race: true,
                  sport_type: 'cycling',
                  from_profile: true
                });
              }
            }
          }
        } catch (cyclingErr) {
          console.log('Error loading cycling profile:', cyclingErr);
        }
        
        setAllEvents(allRaces);
      }
    } catch (error) { console.error('Error loading athlete data:', error); }
    finally { setIsLoading(false); }
  };

  const loadMessages = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/messages?coach_user_id=${coachUserId}`);
      if (res.ok) { const data = await res.json(); setMessages(data.messages || []); }
    } catch (error) { console.error('Error loading messages:', error); }
  };

  const loadExistingPlans = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/plans?coach_user_id=${coachUserId}`);
      if (res.ok) { 
        const data = await res.json(); 
        console.log('Loaded plans:', data);
        
        const categorized = { running: { current: null, next: null }, cycling: { current: null, next: null } };
        
        if (data.all_plans && data.all_plans.length > 0) {
          data.all_plans.forEach(plan => {
            const st = plan.sport_type || 'running';
            const wt = plan.is_current_week ? 'current' : 'next';
            if (!categorized[st][wt]) {
              categorized[st][wt] = plan;
            }
          });
        }
        
        if (data.current_week_plan) {
          const st = data.current_week_plan.sport_type || 'running';
          categorized[st].current = data.current_week_plan;
        }
        if (data.next_week_plan) {
          const st = data.next_week_plan.sport_type || 'running';
          categorized[st].next = data.next_week_plan;
        }
        
        if (data.cycling_current_week_plan) {
          categorized.cycling.current = data.cycling_current_week_plan;
        }
        if (data.cycling_next_week_plan) {
          categorized.cycling.next = data.cycling_next_week_plan;
        }
        
        console.log('Categorized plans:', categorized);
        setExistingPlans(categorized);
      }
    } catch (error) { console.error('Error loading plans:', error); }
  };

  // Load the athlete's full profile (running + cycling combined) into
  // editProfileData so the inline tab form can render with current
  // values. Fires automatically when the profile tab becomes active —
  // no separate "open modal" button is needed anymore.
  const loadCoachEditableProfile = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/full-profile?coach_user_id=${coachUserId}`);
      if (res.ok) {
        const data = await res.json();
        setEditProfileData(data.profile || {});
        coachProfileBaseRef.current = data.profile || {};
      }
    } catch (error) { console.error('Failed to load athlete profile:', error); }
  }, [athlete.user_id, coachUserId]);

  useEffect(() => {
    if (activeTab === 'profile' && !editProfileData) {
      loadCoachEditableProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Coach-editable fields: only the 4 that directly shape the plan
  // structure (level, volume, day distribution, long run). The 5th —
  // training_days_per_week — is derived from preferred_days length
  // at save time, matching Profile.jsx behaviour. Everything else is
  // read-only — the athlete owns it via their own profile screen.
  // Decision 2026-06-01.
  const COACH_EDITABLE_FIELDS = [
    // Running profile core
    'level',
    'avg_weekly_km',
    'preferred_days',
    'preferred_long_run_day',
    // Race goal — these were SHOWN in the UI but never sent (2026-06-10 bug fix)
    'goal_distance',
    'goal_race',
    'goal_date',
    'goal_time',
    // HR + training fields
    'max_hr',
    'strength_training',
    'strength_days_per_week',
    // Injury — same fields the athlete sees in their profile
    'injury_prone_areas',
    'injuries_history',
    'active_injury_severity',
    'injury_sport',
    'injury_description',
    'rehab_status',
    'rehab_week',
    'chronic_sensitivities',
    // Personal records (informational, may inform pace anchors)
    'pr_5k',
    'pr_10k',
    'pr_half',
    'pr_full',
    // Cycling editable fields (2026-06-11 — coach can now tune cycling
    // training structure): level, hours budget, day count, FTP per bike.
    // NOT editable by coach: weight_kg, max_hr (physiological — owned by
    // athlete), race goal fields (athlete's choice), recent_longest_ride_km.
    'cycling_level',
    'weekly_cycling_hours',
    'preferred_cycling_days',
    'ftp_road',
    'ftp_gravel',
    'ftp_mtb',
    'ftp_trainer',
  ];

  // Same day chip layout used in the athlete's Profile.jsx. Hebrew
  // single-letter labels (א..ש), keys are full Hebrew day names so the
  // backend reads the same string format both flows produce.
  const profileDaysOfWeek = [
    { key: 'ראשון', label: 'א' },
    { key: 'שני',   label: 'ב' },
    { key: 'שלישי', label: 'ג' },
    { key: 'רביעי', label: 'ד' },
    { key: 'חמישי', label: 'ה' },
    { key: 'שישי',  label: 'ו' },
    { key: 'שבת',   label: 'ש' },
  ];

  const isCoachDaySelected = (day) => {
    if (!editProfileData?.preferred_days) return false;
    return editProfileData.preferred_days.split(' ').includes(day);
  };

  const handleCoachDayToggle = (day) => {
    const current = editProfileData.preferred_days
      ? editProfileData.preferred_days.split(' ').filter((d) => d) : [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day];
    const order = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    setEditProfileData({ ...editProfileData, preferred_days: next.join(' ') });
  };

  // Cycling-days equivalents (2026-06-11) — same chip behavior as
  // running, just on preferred_cycling_days instead of preferred_days.
  const isCoachCyclingDaySelected = (day) => {
    if (!editProfileData?.preferred_cycling_days) return false;
    return editProfileData.preferred_cycling_days.split(' ').includes(day);
  };

  const handleCoachCyclingDayToggle = (day) => {
    const current = editProfileData.preferred_cycling_days
      ? editProfileData.preferred_cycling_days.split(' ').filter((d) => d) : [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day];
    const order = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    setEditProfileData({ ...editProfileData, preferred_cycling_days: next.join(' ') });
  };

  // Internal save — assumes all validation has already passed (either
  // because the matrix was OK, or because the user explicitly chose
  // "שמור בכל זאת" from the LoadBalanceModal). Pulling this out keeps
  // the override path clean: it doesn't have to duplicate the body.
  const _doSaveProfile = async () => {
    // P0 2026-10-30 / owner rule 2026-10-31: a real change of a plan input rebuilds the athlete's four coming weeks
    // and overwrites earlier changes — asked BEFORE the save; cancel saves nothing.
    if (planRebuildChange(coachProfileBaseRef.current, editProfileData).settings
        && !window.confirm(`${REBUILD_WARNING}

אישור = ${REBUILD_CONFIRM_LABEL}
ביטול = ביטול`)) {
      return;
    }
    setIsSavingProfile(true);
    try {
      const daysCount = editProfileData.preferred_days
        ? editProfileData.preferred_days.split(' ').filter((d) => d).length
        : 0;
      // Send ONLY the 4 plan-shaping fields + derived training_days_per_week
      // + user_id. Server treats anything else as untouched.
      const payload = { user_id: athlete.user_id };
      for (const k of COACH_EDITABLE_FIELDS) {
        if (editProfileData[k] !== undefined) payload[k] = editProfileData[k];
      }
      payload.training_days_per_week = daysCount || null;
      const res = await authFetch(
        `${API_BASE_URL}/coach/athlete/${athlete.user_id}/profile?coach_user_id=${coachUserId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      const data = await res.json();
      if (!data.success) {
        showToast(data.message || 'שגיאה', 'error');
        return;
      }
      loadAthleteData();
      coachProfileBaseRef.current = { ...(coachProfileBaseRef.current || {}), ...editProfileData };

      // The server recorded a real change (days / volume): the athlete's four weeks are rebuilt now — the same
      // rebuild as the athlete's own profile save (coach-authorised for this athlete). One clear outcome message.
      if (data.plan_rebuild && data.plan_rebuild.required) {
        showToast('מעדכן את תכנית המתאמן לפי הפרופיל…', 'info', 4000);
        const out = await runPlanRebuild(athlete.user_id).catch(
          () => ({ ok: false, kind: 'failed', message: 'עדכון התכנית נכשל — התכנית הקודמת נשארה. אפשר לנסות שוב.' }));
        if (out.kind !== 'none') showToast(out.message, out.ok ? 'success' : 'error', out.ok ? 5000 : 9000);
        return;
      }

      // No rebuild required by the server: the profile is saved and the plan is untouched — said as such, never as
      // "the plan was built" (2026-10-31: the coach's profile change rebuilds through the same server rebuild as the
      // athlete's; the athlete-only regenerate-all is not called from here).
      showToast('✅ הפרופיל עודכן.');
    } catch (error) { showToast('שגיאה בשמירה', 'error'); }
    finally { setIsSavingProfile(false); }
  };

  // Public entry — the Save button calls this. Runs the matrix check
  // first; if it fails, opens the LoadBalanceModal instead of saving.
  // The modal's two actions either cancel (close) or call
  // _doSaveProfile directly to override.
  const saveProfileChanges = () => {
    const daysCount = editProfileData.preferred_days
      ? editProfileData.preferred_days.split(' ').filter((d) => d).length
      : 0;
    // Distance-scoped: 10K athletes are gated by the runner-level band +
    // profile envelope (validateProfile10K); other distances keep the legacy
    // km↔days matrix. Mirrors the backend coach-update gate in main.py.
    const matrix = validateKmDaysMatrix(
      editProfileData.avg_weekly_km, daysCount,
      { distance: editProfileData.goal_distance, level: editProfileData.level },
    );
    if (!matrix.ok) {
      if (matrix.errorCode) {
        alert(`⚠️ ${matrix.reason}`);
        return;
      }
      setLoadBalanceModal({
        open: true,
        weeklyKm: editProfileData.avg_weekly_km,
        trainingDays: daysCount,
        requiredDays: matrix.requiredDays,
      });
      return;
    }
    _doSaveProfile();
  };

  const openAddRace = () => {
    setRaceForm({ 
      race_name: '', 
      race_date: '', 
      distance: '', 
      target_time: '', 
      is_goal_race: false, 
      sport_type: racesSportTab
    }); 
    setEditingRace(null); 
    setShowAddRace(true); 
  };
  
  const openEditRace = (race) => { 
    setRaceForm({ 
      race_name: race.race_name || race.name || '', 
      race_date: race.race_date || race.date || '', 
      distance: race.distance || '', 
      target_time: race.target_time || '', 
      is_goal_race: race.is_goal_race || false, 
      sport_type: race.sport_type || 'running' 
    }); 
    setEditingRace(race); 
    setShowAddRace(true); 
  };

  const persistRace = async (overrideGoal = false, isGoalRaceArg) => {
    setIsSavingRace(true);
    try {
      const _isGoalRace = (typeof isGoalRaceArg === 'boolean') ? isGoalRaceArg : !!raceForm.is_goal_race;
      const url = editingRace ? `${API_BASE_URL}/coach/athlete/${athlete.user_id}/races/${editingRace.id}?coach_user_id=${coachUserId}` : `${API_BASE_URL}/coach/athlete/${athlete.user_id}/races?coach_user_id=${coachUserId}`;
      const res = await authFetch(url, {
        method: editingRace ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...raceForm, is_goal_race: _isGoalRace, override_goal: !!overrideGoal }),
      });
      const data = await res.json();
      if (data.success) { showToast(editingRace ? '✅ עודכן!' : '✅ נוסף!'); setShowAddRace(false); setEditingRace(null); loadAthleteData(); }
    } catch (error) { showToast('שגיאה', 'error'); }
    finally { setIsSavingRace(false); }
  };

  // After coach picks Goal Race / Tune-up in the modal, finish the save.
  const finalizeCoachRaceAfterTypePick = async (isGoalRace) => {
    setRaceForm((p) => ({ ...p, is_goal_race: isGoalRace }));
    setCoachRaceTypePicker(false);
    if (raceForm.sport_type !== 'cycling' && isGoalRace) {
      const weeklyKm = parseFloat(athleteData?.profile?.avg_weekly_km);
      const result = validateUserGoal(weeklyKm, raceForm.distance, raceForm.target_time);
      if (result.status !== 'ok') {
        setRaceGuardrail({ result });
        return;
      }
    }
    await persistRace(false, isGoalRace);
  };

  const saveRace = async () => {
    const missing = [];
    if (!raceForm.race_name) missing.push('שם תחרות');
    if (!raceForm.race_date) missing.push('תאריך');
    if (!raceForm.distance) missing.push('מרחק/סוג');
    if (raceForm.sport_type !== 'cycling' && !raceForm.target_time) missing.push('זמן יעד');
    if (missing.length) { showToast('נא למלא:\n• ' + missing.join('\n• '), 'error'); return; }
    // Race date must be today or future — a past date can't be a target.
    {
      const d = new Date(raceForm.race_date + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!Number.isNaN(d.getTime()) && d < today) {
        showToast('⚠️ תאריך התחרות לא יכול להיות בעבר.\nעדכן את התאריך להיום או לעתיד.', 'error');
        return;
      }
    }
    // Cycling: no race-type distinction yet — save directly as tune-up.
    if (raceForm.sport_type === 'cycling') {
      await persistRace(false, false);
      return;
    }
    // Running: open the race type picker — it funnels through
    // finalizeCoachRaceAfterTypePick which runs the guardrail and persists.
    setCoachRaceTypePicker(true);
  };

  const deleteRace = async (raceId, raceName) => {
    if (!window.confirm(`למחוק "${raceName}"?`)) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/races/${raceId}?coach_user_id=${coachUserId}`, { method: 'DELETE' });
      if ((await res.json()).success) { showToast('נמחק'); loadAthleteData(); }
    } catch (error) { console.error(error); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setIsSendingMessage(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/message?coach_user_id=${coachUserId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ athlete_user_id: athlete.user_id, message_text: newMessage, message_type: 'general' }) });
      if ((await res.json()).success) { setNewMessage(''); loadMessages(); showToast('✅ נשלח!'); }
    } catch (error) { console.error(error); }
    finally { setIsSendingMessage(false); }
  };

  const saveNotes = async () => {
    setIsSavingNotes(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.id}/notes?coach_user_id=${coachUserId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: coachNotes }) });
      if (res.ok) { setNotesSaved(true); setTimeout(() => setNotesSaved(false), 2000); }
    } catch (error) { console.error(error); }
    finally { setIsSavingNotes(false); }
  };

  const createPlan = async (weekType, sportType = 'running') => {
    setIsCreatingPlan(true);
    const sportHe = sportType === 'cycling' ? 'רכיבה' : 'ריצה';
    const weekHe = weekType === 'current' ? 'שבוע נוכחי' : 'שבוע הבא';
    try {
      console.log(`Creating ${sportType} plan for ${weekType}`);
      const lastActivityDate = activities.length > 0
        ? (activities[0].date || activities[0].start_date_local || '').slice(0, 10)
        : '';
      const lastActivityParam = lastActivityDate ? `&last_activity_date=${lastActivityDate}` : '';
      // planRegenRequest handles both the legacy sync response and the
      // upcoming async (202 + job_id) response. plan-regen-start/done
      // events fire automatically inside.
      const result = await planRegenRequest({
        url: `${API_BASE_URL}/coach/athlete/${athlete.user_id}/create-plan?coach_user_id=${coachUserId}&week_type=${weekType}&sport_type=${sportType}${lastActivityParam}`,
        body: undefined,  // coach create-plan uses query string params, no body
        pollUserId: athlete.user_id,  // job rows are keyed to the athlete uid
        // the coach's own status route (the athlete's /plan-status is self-only — 403 for a coach)
        statusUrl: (jobId) => `${API_BASE_URL}/coach/athlete/${athlete.user_id}/plan-status/${encodeURIComponent(jobId)}?coach_user_id=${coachUserId}`,
        changedFields: [`תכנית ${sportHe} — ${weekHe}`],
      });
      console.log('Create plan result:', result);
      if (result.success) {
        // Re-fetch the real plan from /coach/athlete/{id}/plans — the
        // helper's result_meta is intentionally light (no full plan
        // JSON), so we always pull canonical state from the DB.
        setPlanSportTab(sportType);
        showToast(`✅ תכנית ${sportHe} ל${weekHe} נוצרה למתאמן!`);
        setTimeout(() => loadExistingPlans(), 500);
      } else {
        console.warn('Create plan failed:', result.error);
        showToast(result.error || 'שגיאה ביצירת תכנית', 'error');
      }
    } catch (error) {
      console.error('Create plan error:', error);
      showToast('שגיאה ביצירת תכנית', 'error');
    }
    finally {
      setIsCreatingPlan(false);
    }
  };

  const startEditingPlan = (planType) => {
    const plan = existingPlans[planSportTab][planType];
    if (!plan) return;
    setEditedPlanText(getPlanText(plan) || '');
    setEditingPlanType(planType);
  };

  const cancelEditingPlan = () => { setEditingPlanType(null); setEditedPlanText(''); };

  const saveEditedPlan = async () => {
    const plan = existingPlans[planSportTab][editingPlanType];
    if (!plan?.id) { showToast('שגיאה', 'error'); return; }
    setIsSavingPlan(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/plan/${plan.id}?coach_user_id=${coachUserId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ plan_text: editedPlanText }) });
      if ((await res.json()).success) {
        // ✅ Directly update state with edited plan text
        setExistingPlans(prev => {
          const updated = {
            running: { ...prev.running },
            cycling: { ...prev.cycling }
          };
          if (updated[planSportTab][editingPlanType]) {
            updated[planSportTab][editingPlanType] = {
              ...updated[planSportTab][editingPlanType],
              workouts_json: JSON.stringify({ plan_text: editedPlanText })
            };
          }
          return updated;
        });
        cancelEditingPlan();
        showToast('✅ התכנית נשמרה בהצלחה!');
        // Refetch in background to ensure consistency
        setTimeout(() => loadExistingPlans(), 500);
      }
    } catch (error) { showToast('שגיאה בשמירה', 'error'); }
    finally { setIsSavingPlan(false); }
  };

  const sendAiChat = async () => {
    if (!aiChatInput.trim()) return;
    const userMessage = aiChatInput.trim();
    setAiChatInput('');
    setAiChatMessages(prev => [...prev, { role: 'user', text: userMessage, time: new Date() }]);
    setIsAiThinking(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/chat?coach_user_id=${coachUserId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: userMessage }) });
      if (res.status === 429 || res.status === 403) {
        const data = await res.json();
        setAiChatMessages(prev => [...prev, { role: 'system', text: `⚠️ ${data.detail}`, time: new Date(), isError: true }]);
        return;
      }
      const data = await res.json();
      if (data.success) setAiChatMessages(prev => [...prev, { role: 'assistant', text: data.response, time: new Date(), planCTA: data.plan_cta || null, missingRaceCTA: data.missing_race_cta || null, ambiguousRacesCTA: data.ambiguous_races_cta || null }]);
      else setAiChatMessages(prev => [...prev, { role: 'system', text: '❌ שגיאה', time: new Date(), isError: true }]);
    } catch (error) { setAiChatMessages(prev => [...prev, { role: 'system', text: '❌ שגיאת תקשורת', time: new Date(), isError: true }]); }
    finally { setIsAiThinking(false); }
  };

  const handleAiChatKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiChat(); } };

  // Detect if a coach-chat assistant message contains a saveable weekly plan
  // (at least 3 distinct day-block headers like "### יום ראשון (24/05)").
  // Returns { weekStart, sportType } or null.
  const detectChatPlan = (text) => {
    if (!text) return null;
    const dayRe = /#{1,4}\s+[^\n]*?\bיום\s+(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)\b[^\n]*?(\d{1,2})[\/.](\d{1,2})(?:[\/.](\d{2,4}))?/g;
    const matches = [...text.matchAll(dayRe)];
    if (matches.length < 3) return null;
    const heDayToIdx = { 'ראשון':0,'שני':1,'שלישי':2,'רביעי':3,'חמישי':4,'שישי':5,'שבת':6 };
    // Take any matched day and back-calculate Sunday of that ISO week
    const m0 = matches[0];
    const day = parseInt(m0[2], 10);
    const month = parseInt(m0[3], 10);
    const yearRaw = m0[4] ? parseInt(m0[4], 10) : new Date().getFullYear();
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    const refDate = new Date(year, month - 1, day);
    if (isNaN(refDate.getTime())) return null;
    const heIdx = heDayToIdx[m0[1]] ?? 0; // expected weekday for that match
    // Sunday of the week containing refDate (Hebrew week starts Sunday)
    const sunday = new Date(refDate);
    sunday.setDate(refDate.getDate() - heIdx);
    const pad = (n) => String(n).padStart(2, '0');
    const weekStart = `${sunday.getFullYear()}-${pad(sunday.getMonth() + 1)}-${pad(sunday.getDate())}`;
    const cyclingMarkers = ['תכנית רכיבה', 'אימון רכיבה', 'FTP', 'טריינר', 'רכיבה ארוכה', 'רכיבה קלה', 'אופניים'];
    const sportType = cyclingMarkers.some(k => text.includes(k)) ? 'cycling' : 'running';
    return { weekStart, sportType };
  };

  const saveCoachChatPlan = async (planText) => {
    const detected = detectChatPlan(planText);
    if (!detected) { showToast('לא זוהתה תכנית בהודעה', 'error'); return; }
    setIsSavingPlan(true);
    try {
      const params = new URLSearchParams({
        plan_text: planText,
        week_start_date: detected.weekStart,
        sport_type: detected.sportType,
        coach_user_id: coachUserId,
      });
      const res = await authFetch(`${API_BASE_URL}/coach/athlete/${athlete.user_id}/plan/save?${params.toString()}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        showToast(`✅ התכנית נשמרה כתכנית ${detected.sportType === 'cycling' ? 'רכיבה' : 'ריצה'} של ${athlete.name} (שבוע ${detected.weekStart})`);
        setTimeout(() => loadExistingPlans(), 500);
      } else {
        showToast(data.detail || 'שגיאה בשמירת התכנית', 'error');
      }
    } catch (e) {
      console.error('saveCoachChatPlan error:', e);
      showToast('שגיאת תקשורת בשמירה', 'error');
    } finally {
      setIsSavingPlan(false);
    }
  };

  // Convert either V1 (plan_text markdown) or V2 (structured days[]) plan
  // into a single readable markdown string for the coach to view/edit.
  // V1 returns the stored plan_text as-is. V2 reconstructs a human-friendly
  // markdown from the structured fields. Bug 2026-05-31: coach saw "אין תוכן"
  // on V2 plans because this function only knew about V1.
  const getPlanText = (plan) => {
    if (!plan?.workouts_json) return null;
    const d = typeof plan.workouts_json === 'string' ? JSON.parse(plan.workouts_json) : plan.workouts_json;
    if (!d || typeof d !== 'object') return null;
    // V1 fast path
    if (d.plan_text) return d.plan_text;
    // V2 — reconstruct readable markdown
    if (Array.isArray(d.days)) {
      const lines = [];
      const phaseLabel = d.phase_label || d.phase || '';
      const wkStart = d.week_start || '';
      const wkEnd = d.week_end || '';
      lines.push(`## תכנית שבועית${phaseLabel ? ` · ${phaseLabel}` : ''}`);
      if (wkStart) lines.push(`📅 ${wkStart}${wkEnd ? ` – ${wkEnd}` : ''}`);
      if (d.total_km != null) lines.push(`📊 סה"כ: ${d.total_km} ק"מ`);
      if (d.estimated_duration_minutes != null) {
        const hrs = Math.floor(d.estimated_duration_minutes / 60);
        const mins = d.estimated_duration_minutes % 60;
        lines.push(`⏱️ זמן משוער: ${hrs}:${String(mins).padStart(2, '0')} שעות`);
      }
      if (d.intro) lines.push('', d.intro);
      lines.push('');
      d.days.forEach((day) => {
        const dn = day.day_name || '';
        const dt = day.date || '';
        const head = `### ${day.icon || ''} ${dn}${dt ? ` (${dt})` : ''}`;
        if (day.is_rest) {
          lines.push(head + ' — 😴 מנוחה');
          lines.push('');
          return;
        }
        lines.push(`${head} — ${day.type || ''}`);
        const meta = [];
        if (day.km != null) meta.push(`${day.km} ק"מ`);
        if (day.duration_min != null) meta.push(`${day.duration_min} דק'`);
        if (day.target_zone) meta.push(`Zone ${day.target_zone}`);
        if (day.rpe_effort != null) meta.push(`RPE ${day.rpe_effort}/10`);
        if (day.ftp_pct) meta.push(`FTP ${day.ftp_pct}`);
        if (day.tss != null) meta.push(`TSS ${day.tss}`);
        if (day.bike_type) meta.push(`אופניים: ${day.bike_type}`);
        if (meta.length) lines.push(meta.join(' · '));
        const pl = day.pace?.label;
        if (pl) lines.push(`קצב: ${pl}`);
        if (Array.isArray(day.structure) && day.structure.length) {
          lines.push('**מבנה האימון:**');
          day.structure.forEach((seg) => {
            const segLabel = seg.pace?.label ? ` (${seg.pace.label})` : '';
            // Same contract as DayCard: `display` is the athlete-facing prescription and replaces
            // the text+km pair. See plan_schema.Segment.display.
            if (seg.display) {
              lines.push(`  - ${seg.display}${segLabel}`);
              return;
            }
            const segKm = seg.km != null ? ` — ${seg.km} ק"מ` : '';
            lines.push(`  - ${seg.segment || seg.description || ''}${segKm}${segLabel}`);
          });
        }
        if (day.description) lines.push('', day.description);
        if (Array.isArray(day.tips) && day.tips.length) {
          lines.push('💡 ' + day.tips.join(' · '));
        }
        if (day.coach_notes) lines.push(`📝 ${day.coach_notes}`);
        lines.push('');
      });
      if (Array.isArray(d.weekly_tips) && d.weekly_tips.length) {
        lines.push('### 📋 טיפים לשבוע');
        d.weekly_tips.forEach((t) => lines.push(`- ${t}`));
      }
      return lines.join('\n');
    }
    return null;
  };
  const formatPace = (p) => { if (!p) return '—'; if (typeof p === 'string') return p; return `${Math.floor(p)}:${Math.round((p - Math.floor(p)) * 60).toString().padStart(2, '0')}`; };
  const formatDuration = (m) => { if (!m) return '—'; const h = Math.floor(m / 60); const mm = Math.round(m % 60); return h > 0 ? `${h}:${mm.toString().padStart(2, '0')}` : `${mm} דק'`; };
  const getDaysUntilRace = (d) => { if (!d) return null; const diff = Math.ceil((new Date(d) - new Date()) / 86400000); return diff > 0 ? diff : null; };
  
  // 🚴🏃 Filter races by sport type
  const filteredRaces = allEvents.filter(race => {
    const raceSport = race.sport_type || 'running';
    return raceSport === racesSportTab;
  });
  
  // Count races by sport
  const runningRacesCount = allEvents.filter(r => (r.sport_type || 'running') === 'running').length;
  const cyclingRacesCount = allEvents.filter(r => r.sport_type === 'cycling').length;
  
  const getGoalRace = (sportType = null) => {
    const racesToSearch = sportType ? allEvents.filter(e => (e.sport_type || 'running') === sportType) : allEvents;
    const goalFromEvents = racesToSearch.find(e => e.is_goal_race);
    if (goalFromEvents) return goalFromEvents;
    
    // Fallback to profile goal race for running
    if ((!sportType || sportType === 'running') && athleteData?.profile?.goal_race) {
      return { 
        name: athleteData.profile.goal_race, 
        date: athleteData.profile.goal_date, 
        target_time: athleteData.profile.goal_time, 
        distance: athleteData.profile.goal_distance, 
        sport_type: 'running' 
      };
    }
    
    // Fallback to profile goal event for cycling
    if ((!sportType || sportType === 'cycling') && cyclingProfile?.cycling_goal_event) {
      return {
        name: cyclingProfile.cycling_goal_event,
        date: cyclingProfile.cycling_goal_date,
        target_time: '',
        distance: cyclingProfile.cycling_goal_distance || '',
        sport_type: 'cycling'
      };
    }
    
    return null;
  };
  
  const goalRace = getGoalRace(racesSportTab);
  
  // Filter out the goal race from list if it's displayed separately above
  const filteredRacesForList = filteredRaces.filter(race => {
    if (!goalRace) return true;
    const raceName = (race.name || race.race_name || '').trim().toLowerCase();
    const goalName = (goalRace.name || goalRace.race_name || '').trim().toLowerCase();
    return raceName !== goalName;
  });

  const renderPlanSection = (planType, plan, borderColor, icon, title) => {
    const isEditing = editingPlanType === planType;
    const planText = getPlanText(plan);
    const sportName = planSportTab === 'cycling' ? 'רכיבה' : 'ריצה';
    
    return (
      <div className={`p-4 rounded-xl bg-[#1e293b] border ${borderColor}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">{icon}</span>
            <p className="text-white font-medium">{title}</p>
            {plan && <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">קיימת</span>}
          </div>
          <div className="flex items-center gap-2">
            {plan && !isEditing && <button onClick={() => startEditingPlan(planType)} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-500">✏️ ערוך</button>}
            {/* "צור" appears ONLY when there's no plan in DB for this week.
                Plans are auto-generated for current+next week on athlete
                login, so this is an edge case (initial generation failed,
                DB hiccup). Once a plan exists the coach edits via the
                blue ✏️ button or via chat — never regenerates from
                scratch (avoids stray LLM costs + drift). */}
            {!plan && (
              <button onClick={() => createPlan(planType, planSportTab)} disabled={isCreatingPlan} className={`px-3 py-1.5 rounded-lg ${planSportTab === 'cycling' ? 'bg-orange-600 hover:bg-orange-500' : 'bg-green-600 hover:bg-green-500'} text-white text-sm disabled:opacity-50`}>
                {isCreatingPlan ? '⏳' : `✨ צור תכנית ${sportName}`}
              </button>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="space-y-3">
            <textarea value={editedPlanText} onChange={(e) => setEditedPlanText(e.target.value)} className="w-full p-3 rounded-lg bg-[#0f172a] border border-white/10 text-gray-300 text-sm resize-none focus:border-blue-500 outline-none" rows={12} />
            <div className="flex items-center gap-2">
              <button onClick={saveEditedPlan} disabled={isSavingPlan} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-500 disabled:opacity-50">{isSavingPlan ? '⏳' : '💾'} שמור</button>
              <button onClick={cancelEditingPlan} disabled={isSavingPlan} className="px-4 py-2 rounded-lg bg-gray-600 text-white text-sm hover:bg-gray-500">ביטול</button>
            </div>
          </div>
        ) : plan ? (
          <div className="p-3 rounded-lg bg-[#0f172a] border border-white/5">
            <div className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto">{planText || 'אין תוכן'}</div>
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-[#0f172a]/50 text-center">
            <p className="text-gray-500 text-sm">אין תכנית {sportName} {planType === 'current' ? 'לשבוע הנוכחי' : 'לשבוע הבא'}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-[#0f172a] z-50 overflow-y-auto" dir="rtl">
      <header className="sticky top-0 bg-[#0f172a]/95 backdrop-blur-sm border-b border-white/5 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={onClose} className="w-10 h-10 rounded-xl bg-[#1e293b] border border-white/10 flex items-center justify-center text-gray-400 hover:text-white">→</button>
              <div><h1 className="text-xl font-bold text-white">{athlete.name}</h1><p className="text-gray-500 text-sm">{athlete.email || 'אין אימייל'}</p></div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {[{ id: 'profile', label: 'פרופיל', icon: '👤' },{ id: 'races', label: 'תחרויות', icon: '🏁' },{ id: 'activities', label: 'אימונים', icon: '🏃' },{ id: 'plan', label: 'תכניות', icon: '📅' },{ id: 'ai', label: 'ייעוץ AI', icon: '🤖' },{ id: 'messages', label: 'הודעות', icon: '💬' }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition whitespace-nowrap ${activeTab === tab.id ? 'text-purple-400 border-b-2 border-purple-400' : 'text-gray-500 hover:text-gray-300'}`}><span className="ml-1">{tab.icon}</span>{tab.label}</button>
            ))}
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {isLoading ? (<div className="text-center py-12"><div className="animate-spin text-4xl mb-4">⏳</div><p className="text-gray-400">טוען...</p></div>) : (
            <>
              {activeTab === 'profile' && (
                <div className="space-y-4">
                  {!editProfileData ? (
                    <div className="text-center py-8 text-gray-500 text-sm">⏳ טוען פרופיל...</div>
                  ) : (
                    <>
                      {(() => {
                        const _sa = (editProfileData.sports_active ?? 'running');
                        const _hasRun = _sa.includes('running') || !_sa;
                        const _hasCyc = _sa.includes('cycling');
                        if (_hasRun && _hasCyc) {
                          return (
                            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs leading-relaxed">
                              ✏️ <b>שדות פתוחים לעריכה:</b> <b>רמה</b>, <b>ק"מ שבועי</b>, <b>ימי אימון</b>, <b>יום ריצה ארוכה</b>.<br />
                              שדות רכיבה — לצפייה בלבד. שאר השדות בבעלות המתאמן.
                            </div>
                          );
                        }
                        if (_hasCyc && !_hasRun) {
                          return (
                            <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-200 text-xs leading-relaxed">
                              🚴 פרופיל המתאמן הוא <b>רכיבה בלבד</b>. כל שדות הרכיבה — לצפייה בלבד. שינויים מבוצעים ע"י המתאמן בצד שלו.
                            </div>
                          );
                        }
                        return (
                          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs leading-relaxed">
                            ✏️ <b>למאמן פתוחים לעריכה 5 שדות</b> בפרופיל ריצה: <b>רמה</b>, <b>ק"מ שבועי</b>, <b>אימונים בשבוע</b>, <b>ימי אימון מועדפים</b>, <b>יום ריצה ארוכה</b>.<br />
                            שאר השדות (תחרות יעד, פציעות, וכו') בבעלות המתאמן — לצפייה בלבד.
                          </div>
                        );
                      })()}

                      {/* Header: name + age (read-only) */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-400 text-xs mb-1">שם</label>
                          <input type="text" value={editProfileData.name || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-xs mb-1">גיל</label>
                          <input type="number" value={editProfileData.age || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                        </div>
                      </div>

                      {/* 🏃 Running card — only show when running is in sports_active.
                          2026-06-10 bug: was unconditional → coach saw "פרופיל ריצה"
                          for cycling-only athletes. Default to running when
                          sports_active is empty/null (legacy single-sport users). */}
                      {(() => {
                        const _sa = (editProfileData.sports_active ?? 'running');
                        return _sa.includes('running') || !_sa;
                      })() && (
                      <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                        <h3 className="text-cyan-400 font-bold mb-4 flex items-center gap-2"><span>🏃</span> פרופיל ריצה</h3>

                        {/* Row 1: Level + Weekly KM — BOTH EDITABLE */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">רמה (לפי ק"מ שבועי)</label>
                            <select value={editProfileData.level || ''} onChange={(e) => setEditProfileData({ ...editProfileData, level: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm">
                              <option value="">בחר רמה...</option>
                              <option value="beginner">🌱 מתחיל (עד 25 ק"מ)</option>
                              <option value="experienced">💪 מנוסה (26–40 ק"מ)</option>
                              <option value="advanced">🔥 מתקדם (41+ ק"מ)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">ק"מ שבועי <span className="text-red-400">*</span></label>
                            <input type="number" value={editProfileData.avg_weekly_km || ''} onChange={(e) => setEditProfileData({ ...editProfileData, avg_weekly_km: e.target.value })} placeholder="40" className={`w-full p-2 rounded-lg bg-[#0f172a] border text-white text-sm ${editProfileData.avg_weekly_km ? 'border-white/10' : 'border-red-500/40'}`} />
                          </div>
                        </div>

                        {/* Row 2: Goal Race + Distance — READ-ONLY */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">שם מרוץ יעד</label>
                            <input type="text" value={editProfileData.goal_race || ''} readOnly placeholder="מרתון תל אביב" className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                          </div>
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">מרחק יעד</label>
                            <select value={editProfileData.goal_distance || ''} disabled className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed">
                              <option value="">בחר מרחק...</option>
                              <option value="5">5 ק"מ</option>
                              <option value="10">10 ק"מ</option>
                              <option value="21.1">חצי מרתון</option>
                              <option value="42.2">מרתון</option>
                            </select>
                          </div>
                        </div>

                        {/* Row 3: Goal Date + Goal Time — READ-ONLY */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">תאריך מרוץ</label>
                            <input type="date" value={editProfileData.goal_date || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                          </div>
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">זמן יעד</label>
                            <input type="text" value={editProfileData.goal_time || ''} readOnly placeholder="בחר זמן יעד" className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                          </div>
                        </div>

                        {/* Max HR — READ-ONLY */}
                        <div className="mb-4">
                          <label className="block text-gray-400 text-xs mb-1">דופק מקסימלי (Max HR)</label>
                          <input type="number" value={editProfileData.max_hr || ''} readOnly placeholder="185" className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                        </div>

                        {/* Preferred Running Days — EDITABLE chips */}
                        <div className="mb-4">
                          <label className="block text-gray-400 text-xs mb-2">ימי ריצה מועדפים</label>
                          <div className="flex gap-2">
                            {profileDaysOfWeek.map((day) => (
                              <button key={day.key} type="button" onClick={() => handleCoachDayToggle(day.key)} className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                                isCoachDaySelected(day.key)
                                  ? 'bg-cyan-500 text-white'
                                  : 'bg-[#0f172a] border border-white/10 text-gray-400 hover:border-cyan-500/50'
                              }`}>
                                {day.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Preferred Long Run Day — EDITABLE chips */}
                        <div className="mb-4">
                          <label className="block text-gray-400 text-xs mb-2">יום ריצה ארוכה מועדף</label>
                          <div className="flex gap-2">
                            {profileDaysOfWeek.map((day) => (
                              <button key={day.key} type="button" onClick={() => setEditProfileData({ ...editProfileData, preferred_long_run_day: editProfileData.preferred_long_run_day === day.key ? '' : day.key })} className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                                editProfileData.preferred_long_run_day === day.key
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-[#0f172a] border border-white/10 text-gray-400 hover:border-orange-500/50'
                              }`}>
                                {day.label}
                              </button>
                            ))}
                          </div>
                          <p className="text-gray-600 text-xs mt-1">לא חובה — אם לא תבחר, נשים ביום האחרון שלך</p>
                        </div>

                        {/* Strength training — READ-ONLY visual */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className={`p-3 rounded-lg flex items-center gap-3 cursor-not-allowed opacity-70 ${editProfileData.strength_training ? 'bg-green-500/20 border border-green-500/30' : 'bg-[#0f172a]/60 border border-white/5'}`}>
                            <span className="text-xl">💪</span>
                            <div className="flex-1">
                              <p className="text-white text-sm font-medium">אימוני כוח</p>
                              <p className="text-gray-400 text-xs">{editProfileData.strength_training ? `${editProfileData.strength_days_per_week || 1} פעמים בשבוע` : 'לא מתאמן'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Injuries — READ-ONLY */}
                        <div>
                          <label className="block text-gray-400 text-xs mb-1">אזורים רגישים / היסטוריית פציעות</label>
                          <textarea value={editProfileData.injuries_history || editProfileData.injury_prone_areas || ''} readOnly rows={2} placeholder="למשל: ברך ימין, גיד אכילס..." className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm resize-none cursor-not-allowed" />
                        </div>
                      </div>
                      )}

                      {/* 🩹 Injury lifecycle — coach edits with the SAME
                          unified-status dropdown the athlete sees. 7 options
                          covering acute (none/mild/medium/severe) and rehab
                          (3 weeks). Single source of truth: same backend
                          fields the athlete writes to. */}
                      {(() => {
                        // Same _uiStatus / _applyUiStatus logic as Profile.jsx
                        const _uiStatus = (() => {
                          if (editProfileData.active_injury_severity) return editProfileData.active_injury_severity;
                          if (editProfileData.rehab_status === 'improving' && editProfileData.rehab_week) {
                            return `rehab_w${editProfileData.rehab_week}`;
                          }
                          return 'none';
                        })();
                        const _isActive = _uiStatus !== 'none';
                        const _applyUiStatus = (next) => {
                          if (next === 'none') {
                            setEditProfileData({
                              ...editProfileData,
                              active_injury_severity: '',
                              rehab_status: '',
                              rehab_week: '',
                              injury_sport: '',
                              injury_description: '',
                            });
                            return;
                          }
                          if (next === 'mild' || next === 'medium' || next === 'severe') {
                            setEditProfileData({
                              ...editProfileData,
                              active_injury_severity: next,
                              rehab_status: '',
                              rehab_week: '',
                            });
                            return;
                          }
                          if (next.startsWith('rehab_w')) {
                            setEditProfileData({
                              ...editProfileData,
                              active_injury_severity: '',
                              rehab_status: 'improving',
                              rehab_week: next.replace('rehab_w', ''),
                            });
                            return;
                          }
                        };
                        return (
                          <div className="p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-pink-500/10 border border-red-500/20">
                            <h3 className="text-red-400 font-bold mb-2 flex items-center gap-2"><span>🩹</span> פציעה פעילה / שיקום ✏️</h3>
                            <p className="text-gray-500 text-[10px] mb-3 leading-snug">
                              בחר את המצב הנוכחי. התכניות יתאימו את עצמן אוטומטית
                              (מנוחה מלאה / עצימות מופחתת / חזרה הדרגתית).
                            </p>
                            <div className="mb-3">
                              <label className="block text-gray-400 text-xs mb-1">מה מצב הפציעה הנוכחי?</label>
                              <select
                                value={_uiStatus}
                                onChange={(e) => _applyUiStatus(e.target.value)}
                                className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm"
                              >
                                <option value="none">🟢 אין פציעה פעילה / הכל כרגיל</option>
                                <option value="mild">🩹 קלה — להמשיך באימונים קלים (נפח −30%)</option>
                                <option value="medium">⚠️ בינונית — עצימות מופחתת + קרוס-טריינינג (נפח −50%)</option>
                                <option value="severe">🚨 חמורה — מנוחה מלאה והשבתה</option>
                                <option value="rehab_w1">🏥 שיקום — שבוע 1/3 (50% נפח, RPE ≤ 3)</option>
                                <option value="rehab_w2">🏥 שיקום — שבוע 2/3 (75% נפח, מתגברות קצרות)</option>
                                <option value="rehab_w3">🏥 שיקום — שבוע 3/3 (90% נפח, איכות מתונה בודדת)</option>
                              </select>
                            </div>
                            {_isActive && (
                              <>
                                <div className="mb-3">
                                  <label className="block text-gray-400 text-xs mb-1">
                                    איזה ספורט מושפע? <span className="text-red-400">* חובה</span>
                                  </label>
                                  <select
                                    value={editProfileData.injury_sport || ''}
                                    onChange={(e) => setEditProfileData({ ...editProfileData, injury_sport: e.target.value })}
                                    className={`w-full p-2 rounded-lg bg-[#0f172a] text-white text-sm border ${
                                      !editProfileData.injury_sport ? 'border-red-400/60' : 'border-white/10'
                                    }`}
                                  >
                                    <option value="">בחר ספורט</option>
                                    <option value="running">ריצה בלבד</option>
                                    {CYCLING_ENABLED && <option value="cycling">רכיבה בלבד</option>}
                                    {CYCLING_ENABLED && <option value="both">גם ריצה וגם רכיבה</option>}
                                  </select>
                                  {!editProfileData.injury_sport && (
                                    <p className="text-red-400 text-[10px] mt-1">
                                      חובה לבחור ספורט — בלי זה, התכנית לא יודעת איך להתאים את עצימות האימונים לפציעה.
                                    </p>
                                  )}
                                </div>
                                <div className="mb-3">
                                  <label className="block text-gray-400 text-xs mb-1">מה בדיוק כואב? (תיאור חופשי)</label>
                                  <input
                                    type="text"
                                    value={editProfileData.injury_description || ''}
                                    onChange={(e) => setEditProfileData({ ...editProfileData, injury_description: e.target.value })}
                                    placeholder="למשל: שין ספלינטס שמאל, דלקת בגיד אכילס, רנר'ס ני"
                                    className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm"
                                  />
                                </div>
                              </>
                            )}
                            <div>
                              <label className="block text-gray-400 text-xs mb-1">רגישויות כרוניות (היסטוריה, לא פעילה כעת)</label>
                              <input
                                type="text"
                                value={editProfileData.chronic_sensitivities || ''}
                                onChange={(e) => setEditProfileData({ ...editProfileData, chronic_sensitivities: e.target.value })}
                                placeholder="למשל: כאבי ברך ימין בעבר, נטייה לפציעת אכילס"
                                className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm"
                              />
                              <p className="text-gray-500 text-[10px] mt-1 leading-snug">
                                לא משנה נפח/עצימות — רק מוסיף טיפים מונעים לתכנית.
                              </p>
                            </div>
                            <p className="text-xs text-amber-300/80 mt-3">💡 שינויי פציעה מפעילים יצירת תכנית חדשה אוטומטית</p>
                          </div>
                        );
                      })()}

                      {/* 🚴 Cycling card — training structure fields EDITABLE,
                          race/physiological fields READ-ONLY (athlete owned). */}
                      {(editProfileData.sports_active || '').includes('cycling') && (
                        <div className="p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20">
                          <h3 className="text-orange-400 font-bold mb-4 flex items-center gap-2"><span>🚴</span> פרופיל רכיבה</h3>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-gray-400 text-xs mb-1">רמה (רכיבה) ✏️</label>
                              <select value={editProfileData.cycling_level || ''} onChange={(e) => setEditProfileData({ ...editProfileData, cycling_level: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm">
                                <option value="">בחר רמה...</option>
                                {CYCLING_LEVEL_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-gray-400 text-xs mb-1">FTP כללי (W) — read-only</label>
                              <input type="number" value={editProfileData.ftp || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div><label className="block text-gray-400 text-xs mb-1">FTP כביש (W) ✏️</label><input type="number" value={editProfileData.ftp_road || ''} onChange={(e) => setEditProfileData({ ...editProfileData, ftp_road: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
                            <div><label className="block text-gray-400 text-xs mb-1">FTP טריינר (W) ✏️</label><input type="number" value={editProfileData.ftp_trainer || ''} onChange={(e) => setEditProfileData({ ...editProfileData, ftp_trainer: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div><label className="block text-gray-400 text-xs mb-1">FTP גראבל (W) ✏️</label><input type="number" value={editProfileData.ftp_gravel || ''} onChange={(e) => setEditProfileData({ ...editProfileData, ftp_gravel: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
                            <div><label className="block text-gray-400 text-xs mb-1">FTP הרים (W) ✏️</label><input type="number" value={editProfileData.ftp_mtb || ''} onChange={(e) => setEditProfileData({ ...editProfileData, ftp_mtb: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div><label className="block text-gray-400 text-xs mb-1">שעות רכיבה שבועיות ✏️</label><input type="number" step="0.5" value={editProfileData.weekly_cycling_hours || ''} onChange={(e) => setEditProfileData({ ...editProfileData, weekly_cycling_hours: e.target.value })} className="w-full p-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
                            <div><label className="block text-gray-400 text-xs mb-1">משקל (ק"ג) — read-only</label><input type="number" value={editProfileData.weight_kg || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" /></div>
                          </div>
                          <div className="mb-4">
                            <label className="block text-gray-400 text-xs mb-2">ימי רכיבה מועדפים ✏️</label>
                            <div className="flex gap-2">
                              {profileDaysOfWeek.map((day) => (
                                <button key={day.key} type="button"
                                  onClick={() => handleCoachCyclingDayToggle(day.key)}
                                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                                    isCoachCyclingDaySelected(day.key)
                                      ? 'bg-orange-500 text-white'
                                      : 'bg-[#0f172a] border border-white/10 text-gray-400 hover:border-orange-500/50'
                                  }`}>
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div><label className="block text-gray-400 text-xs mb-1">אירוע יעד — read-only</label><input type="text" value={editProfileData.cycling_goal_event || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" /></div>
                            <div><label className="block text-gray-400 text-xs mb-1">תאריך אירוע — read-only</label><input type="date" value={editProfileData.cycling_goal_date || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" /></div>
                          </div>
                          <div>
                            <label className="block text-gray-400 text-xs mb-1">מרחק יעד — read-only</label>
                            <input type="text" value={editProfileData.cycling_goal_distance || ''} readOnly className="w-full p-2 rounded-lg bg-[#0f172a]/60 border border-white/5 text-gray-400 text-sm cursor-not-allowed" />
                          </div>
                        </div>
                      )}

                      {/* Save button. Stays enabled and gradient-styled
                          even when the km × days matrix would fail —
                          on click, saveProfileChanges either runs the
                          save (matrix OK) or opens LoadBalanceModal
                          (matrix fails). The modal stops the user with
                          a clear explanation; no inline error text or
                          disabled-button-with-warning here. */}
                      {/* Save button enabled when EITHER sport has its
                          required volume field filled. 2026-06-11 bug:
                          condition required avg_weekly_km (running) so
                          cycling-only athletes had a permanently
                          disabled save button. Now: running needs
                          avg_weekly_km, cycling needs weekly_cycling_hours,
                          and either being present unlocks save. */}
                      {(() => {
                        const _sa = String(editProfileData.sports_active || 'running');
                        const _hasRun = _sa.includes('running') || !_sa;
                        const _hasCyc = _sa.includes('cycling');
                        const _runOk = !_hasRun || !!editProfileData.avg_weekly_km;
                        const _cycOk = !_hasCyc || !!editProfileData.weekly_cycling_hours;
                        const _isDisabled = isSavingProfile || !(_runOk && _cycOk);
                        return (
                          <button
                            onClick={saveProfileChanges}
                            disabled={_isDisabled}
                            className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-500 text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSavingProfile ? '⏳ שומר...' : '💾 שמור שינויים'}
                          </button>
                        );
                      })()}
                    </>
                  )}

                  {/* Coach notes card — independent of profile, always visible */}
                  <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5">
                    <p className="text-gray-500 text-xs mb-2">📝 הערות מאמן</p>
                    <textarea className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm resize-none" rows={3} placeholder="הוסף הערות..." value={coachNotes} onChange={(e) => setCoachNotes(e.target.value)} />
                    <div className="flex items-center justify-between mt-2">
                      <button onClick={saveNotes} disabled={isSavingNotes} className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-500 disabled:opacity-50">{isSavingNotes ? '⏳' : '💾'} שמור</button>
                      {notesSaved && <span className="text-green-400 text-sm">✓ נשמר!</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* 🚴🏃 UPDATED RACES TAB WITH SPORT TYPE TABS */}
              {activeTab === 'races' && (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-bold text-white">🏁 לוח תחרויות</h2>
                      <button onClick={openAddRace} className={`px-4 py-2 rounded-xl text-white text-sm hover:opacity-90 ${racesSportTab === 'cycling' ? 'bg-orange-600' : 'bg-purple-600'}`}>
                        ➕ הוסף {racesSportTab === 'cycling' ? 'תחרות רכיבה' : 'מרוץ'}
                      </button>
                    </div>
                  
                    {/* 🚴🏃 Sport Type Tabs */}
                    <div className="flex bg-[#1e293b] rounded-xl p-1 border border-white/5">
                      <button 
                        onClick={() => setRacesSportTab('running')} 
                        className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                          racesSportTab === 'running' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        <span>🏃</span>
                        <span>מרוצי ריצה</span>
                        {runningRacesCount > 0 && (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${racesSportTab === 'running' ? 'bg-white/20' : 'bg-gray-700'}`}>
                            {runningRacesCount}
                          </span>
                        )}
                      </button>
                      {CYCLING_ENABLED && (
                        <button
                          onClick={() => setRacesSportTab('cycling')}
                          className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                            racesSportTab === 'cycling' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          <span>🚴</span>
                          <span>תחרויות רכיבה</span>
                          {cyclingRacesCount > 0 && (
                            <span className={`px-2 py-0.5 rounded-full text-xs ${racesSportTab === 'cycling' ? 'bg-white/20' : 'bg-gray-700'}`}>
                              {cyclingRacesCount}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  
                    {/* Races List - Filtered by sport */}
                    {filteredRaces.length > 0 ? (
                      <div className="space-y-2">
                        {filteredRaces.map((race) => {
                          const isPast = new Date(race.date || race.race_date) < new Date();
                          const isCycling = race.sport_type === 'cycling';
                          const isFromProfile = race.from_profile;
                          const daysUntil = getDaysUntilRace(race.date || race.race_date);
                          return (
                            <div key={race.id} className={`p-4 rounded-xl ${
                              race.is_goal_race 
                                ? 'bg-yellow-500/10 border border-yellow-500/20' 
                                : isPast 
                                  ? 'bg-gray-500/10 border border-gray-500/20' 
                                  : isCycling 
                                    ? 'bg-orange-500/10 border border-orange-500/20' 
                                    : 'bg-[#1e293b] border border-white/5'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">
                                    {race.is_goal_race ? '⭐' : isPast ? '✓' : isCycling ? '🚴' : '🏃'}
                                  </span>
                                  <div>
                                    <p className={`font-medium ${isPast ? 'text-gray-500' : 'text-white'}`}>
                                      {race.name || race.race_name}
                                      {!isPast && (
                                        race.is_goal_race
                                          ? <span className="text-yellow-400 text-xs mr-2 px-1.5 py-0.5 rounded bg-yellow-500/10 border border-yellow-500/30">🎯 מטרה</span>
                                          : <span className="text-cyan-300 text-xs mr-2 px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30">🏃 אימון</span>
                                      )}
                                      {isFromProfile && <span className="text-orange-400 text-xs mr-2">(מהפרופיל)</span>}
                                    </p>
                                    <p className="text-gray-500 text-sm">
                                      {race.distance ? `${race.distance} | ` : ''}{race.date || race.race_date}
                                      {daysUntil && !isPast && <span className="text-yellow-400 mr-2"> • {daysUntil} ימים</span>}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {race.target_time && !isPast && (
                                    <span className="text-cyan-400 text-sm">יעד: {race.target_time}</span>
                                  )}
                                  {!isFromProfile ? (
                                    <>
                                      <button onClick={() => openEditRace(race)} className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 flex items-center justify-center">✏️</button>
                                      <button onClick={() => deleteRace(race.id, race.name || race.race_name)} className="w-8 h-8 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center">🗑️</button>
                                    </>
                                  ) : (
                                    <span className="text-xs text-gray-500">ערוך בפרופיל</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12 rounded-xl bg-[#1e293b] border border-white/5">
                        <span className="text-4xl mb-4 block">{racesSportTab === 'cycling' ? '🚴' : '🏁'}</span>
                        <p className="text-gray-400 mb-2">
                          {racesSportTab === 'cycling' ? 'אין תחרויות רכיבה' : 'אין מרוצי ריצה'}
                        </p>
                        <button onClick={openAddRace} className={`${racesSportTab === 'cycling' ? 'text-orange-400 hover:text-orange-300' : 'text-purple-400 hover:text-purple-300'}`}>
                          ➕ הוסף {racesSportTab === 'cycling' ? 'תחרות רכיבה' : 'מרוץ'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Calendar Sidebar */}
                  <div className="md:col-span-1">
                    <div className="sticky top-4">
                      <EnhancedCalendar events={allEvents} workouts={calendarWorkouts} />
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'activities' && (() => {
                // Sport classifier — same logic as athlete's ActivityList.
                const isCycling = (a) => {
                  const t = (a.type || a.sport_type || a.activityType || '').toLowerCase();
                  const n = (a.activityName || a.name || '').toLowerCase();
                  return t.includes('ride') || t.includes('bike') || t.includes('cycling')
                    || n.includes('ride') || n.includes('cycling') || n.includes('רכיב');
                };
                const isRunning = (a) => !isCycling(a);
                const runCount = activities.filter(isRunning).length;
                const cycCount = activities.filter(isCycling).length;
                const filtered = activitiesFilter === 'running' ? activities.filter(isRunning)
                  : activitiesFilter === 'cycling' ? activities.filter(isCycling)
                  : activities;

                // Build 12-week bar chart: { running, cycling } per week.
                const _today = new Date();
                const weekData = [];
                for (let w = 11; w >= 0; w--) {
                  const wStart = new Date(_today); wStart.setDate(_today.getDate() - (w * 7) - _today.getDay());
                  const wEnd = new Date(wStart); wEnd.setDate(wStart.getDate() + 6);
                  let run = 0, cyc = 0;
                  for (const a of activities) {
                    const ad = new Date(a.date || a.start_date_local || 0);
                    if (ad >= wStart && ad <= wEnd) {
                      const km = parseFloat(a.distance || a.distance_km || 0);
                      if (isCycling(a)) cyc += km; else run += km;
                    }
                  }
                  weekData.push({
                    label: `${wStart.getDate()}/${wStart.getMonth()+1}`,
                    run: Math.round(run), cyc: Math.round(cyc), total: Math.round(run + cyc),
                  });
                }
                const maxBar = Math.max(1, ...weekData.map(w => w.total));

                return (
                <div className="space-y-3">
                  {/* Header with title */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2">
                      🏃 האימונים של {athlete.athlete_name || 'המתאמן'}
                    </h3>
                    <span className="text-xs text-gray-500 bg-white/5 px-3 py-1.5 rounded-lg">
                      {activities.length} אימונים
                    </span>
                  </div>

                  {activities.length === 0 ? (
                    <div className="text-center py-12">
                      <span className="text-4xl mb-4 block">🏃</span>
                      <p className="text-gray-400">אין אימונים</p>
                    </div>
                  ) : (
                  <>
                  {/* Tabs filter */}
                  <div className="flex gap-2 bg-[#1e293b] rounded-xl p-1 mb-4">
                    {[
                      { key: 'all',     label: `הכל (${activities.length})` },
                      { key: 'running', label: `🏃 ריצה (${runCount})` },
                      { key: 'cycling', label: `🚴 רכיבה (${cycCount})` },
                    ].map(tab => (
                      <button key={tab.key} onClick={() => setActivitiesFilter(tab.key)}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                          activitiesFilter === tab.key
                            ? 'bg-[#0f172a] text-cyan-400'
                            : 'text-gray-500 hover:text-gray-300'
                        }`}>
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* 12-week km bar chart */}
                  <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-bold text-sm">ק"מ לפי שבוע</span>
                      <span className="text-xs text-gray-500">12 שבועות</span>
                    </div>
                    <div className="flex items-end gap-1 h-32">
                      {weekData.map((w, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center justify-end gap-0.5">
                          {w.total > 0 && (
                            <span className="text-[10px] text-gray-400 mb-1">{w.total}</span>
                          )}
                          <div className="w-full flex flex-col-reverse gap-0.5">
                            {w.run > 0 && (
                              <div className="w-full bg-cyan-500 rounded-t" style={{ height: `${(w.run / maxBar) * 100}px` }} />
                            )}
                            {w.cyc > 0 && (
                              <div className="w-full bg-orange-500" style={{ height: `${(w.cyc / maxBar) * 100}px` }} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[10px] text-gray-500">
                      <span>{weekData[0]?.label}</span>
                      <span>{weekData[Math.floor(weekData.length/2)]?.label}</span>
                      <span>{weekData[weekData.length-1]?.label}</span>
                    </div>
                    <div className="flex justify-end gap-3 mt-2 text-[10px]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-cyan-500 rounded-sm" /> ריצה</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded-sm" /> רכיבה</span>
                    </div>
                  </div>

                  {/* Activities list (filtered) */}
                  {filtered.map((act, i) => {
                      const activityId = act.activityId || act.id;
                      const splits = activitySplits[activityId] || act.splits || [];
                      const _isCyc = isCycling(act);

                      return (
                      <div key={i} className="p-4 rounded-xl bg-[#1e293b] border border-white/5">
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            if (expandedActivity === i) {
                              setExpandedActivity(null);
                            } else {
                              setExpandedActivity(i);
                              if (activityId) fetchSplitsForActivity(activityId);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                _isCyc ? 'bg-orange-500/20 text-orange-400'
                                       : 'bg-cyan-500/20 text-cyan-400'
                              }`}>
                                {_isCyc ? '🚴 רכיבה' : '🏃 ריצה'}
                              </span>
                              <span className="text-white font-medium">{act.activityName || act.name || (_isCyc ? 'רכיבה' : 'ריצה')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 text-sm">{act.date}</span>
                              {splits.length > 0 && (
                                <span className="text-cyan-400 text-xs">📊 {splits.length} ספליטים</span>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div><p className={`font-bold ${_isCyc ? 'text-orange-400' : 'text-cyan-400'}`}>{act.distance || act.distance_km || '—'}</p><p className="text-gray-500 text-xs">ק"מ</p></div>
                            <div><p className="text-white font-bold">{formatDuration(act.duration ? act.duration / 60 : act.duration_minutes)}</p><p className="text-gray-500 text-xs">זמן</p></div>
                            <div><p className="text-purple-400 font-bold">{act.averagePace || formatPace(act.avg_pace) || (_isCyc ? `${act.avg_speed || '—'}` : '—')}</p><p className="text-gray-500 text-xs">{_isCyc ? 'קמ"ש' : 'קצב'}</p></div>
                            <div><p className="text-red-400 font-bold">{act.averageHR || act.avg_hr || '—'}</p><p className="text-gray-500 text-xs">דופק</p></div>
                          </div>
                        </div>

                        {/* Splits Section - Expandable with on-demand loading */}
                        {expandedActivity === i && loadingSplits === activityId && (
                          <div className="mt-4 pt-4 border-t border-white/10 text-center">
                            <p className="text-gray-500 text-sm animate-pulse">⏳ טוען ספליטים...</p>
                          </div>
                        )}

                        {expandedActivity === i && loadingSplits !== activityId && splits.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <h4 className="text-gray-400 text-sm mb-3">📊 ספליטים לפי קילומטר</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                              {splits.map((split, idx) => (
                                <div key={idx} className="p-2 rounded-lg bg-[#0f172a] border border-white/5 text-center">
                                  <p className="text-gray-500 text-xs mb-1">ק"מ {split.lap_number || split.splitNumber || idx + 1}</p>
                                  <p className="text-purple-400 font-bold text-sm">{split.pace || split.averagePace || '—'}</p>
                                  {(split.averageHR || split.avg_hr) && <p className="text-red-400 text-xs mt-1">❤️ {split.averageHR || split.avg_hr}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {expandedActivity === i && loadingSplits !== activityId && splits.length === 0 && (
                          <div className="mt-4 pt-4 border-t border-white/10 text-center">
                            <p className="text-gray-500 text-sm">אין נתוני ספליטים לאימון זה</p>
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </>
                  )}
                </div>
                );
              })()}

              {activeTab === 'plan' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 space-y-4">
                      <div className="flex bg-[#1e293b] rounded-xl p-1 border border-white/5">
                        <button onClick={() => { setPlanSportTab('running'); setEditingPlanType(null); }} className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${planSportTab === 'running' ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                          <span>🏃</span><span>ריצה</span>
                          {(existingPlans.running.current || existingPlans.running.next) && <span className="w-2 h-2 bg-green-400 rounded-full"></span>}
                        </button>
                        {CYCLING_ENABLED && (
                          <button onClick={() => { setPlanSportTab('cycling'); setEditingPlanType(null); }} className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${planSportTab === 'cycling' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                            <span>🚴</span><span>רכיבה</span>
                            {(existingPlans.cycling.current || existingPlans.cycling.next) && <span className="w-2 h-2 bg-orange-400 rounded-full"></span>}
                          </button>
                        )}
                      </div>
                      {renderPlanSection('current', existingPlans[planSportTab].current, planSportTab === 'cycling' ? 'border-orange-500/20' : 'border-green-500/20', '📆', 'שבוע נוכחי')}
                      {renderPlanSection('next', existingPlans[planSportTab].next, planSportTab === 'cycling' ? 'border-orange-500/30' : 'border-green-500/30', '📅', 'שבוע הבא')}
                      {isCreatingPlan && <div className="text-center py-8 rounded-xl bg-[#1e293b] border border-white/10"><div className="animate-spin text-4xl mb-4">⏳</div><p className="text-gray-400">יוצר תכנית {planSportTab === 'cycling' ? 'רכיבה' : 'ריצה'}...</p></div>}
                    </div>
                    <div className="md:col-span-1">
                      <div className="sticky top-4">
                        <EnhancedCalendar events={allEvents} workouts={calendarWorkouts} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'ai' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">🤖</span>
                        <div><h3 className="text-white font-bold">ייעוץ AI על {athlete.name}</h3><p className="text-gray-400 text-sm">{aiChatMessages.length > 0 ? `${aiChatMessages.length} הודעות` : 'שאל שאלות'}</p></div>
                      </div>
                      {aiChatMessages.length > 0 && <button onClick={clearChatHistory} className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30">🗑️ נקה</button>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['האם הוא מוכן למרוץ?','מה דעתך על הריצה האחרונה?','מה כדאי לשפר?','האם העומס מתאים?'].map((q, i) => (
                      <button key={i} onClick={() => setAiChatInput(q)} className="px-3 py-1.5 rounded-lg bg-[#1e293b] border border-white/10 text-gray-400 text-sm hover:border-purple-500/50 hover:text-white">{q}</button>
                    ))}
                  </div>
                  <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5 min-h-[300px] max-h-[400px] overflow-y-auto">
                    {isChatHistoryLoading ? <div className="text-center py-12"><div className="animate-spin text-3xl mb-4">⏳</div><p className="text-gray-500">טוען...</p></div> : aiChatMessages.length === 0 ? <div className="text-center py-12"><span className="text-5xl mb-4 block">💬</span><p className="text-gray-500">שאל שאלה</p></div> : (
                      <div className="space-y-4">
                        {aiChatMessages.map((msg, i) => {
                          const planInfo = (msg.role === 'assistant' && !msg.isError) ? detectChatPlan(msg.text) : null;
                          return (
                          <div key={i} className="flex justify-start">
                            <div className={`max-w-[90%] p-3 rounded-xl ${msg.role === 'user' ? 'bg-purple-600/20 border border-purple-500/30' : msg.isError ? 'bg-red-600/20 border border-red-500/30' : 'bg-[#0f172a] border border-white/10'}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm">{msg.role === 'user' ? '👨‍🏫' : msg.isError ? '⚠️' : '🤖'}</span>
                                <span className="text-gray-500 text-xs">{msg.role === 'user' ? 'אתה' : msg.isError ? 'מערכת' : 'Claude AI'}</span>
                              </div>
                              <p className={`text-sm whitespace-pre-wrap leading-relaxed ${msg.isError ? 'text-red-300' : 'text-gray-300'}`}>{msg.text}</p>
                              {msg.ambiguousRacesCTA && Array.isArray(msg.ambiguousRacesCTA.options) && msg.ambiguousRacesCTA.options.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-white/10">
                                  <div className="text-xs text-gray-400 mb-2">
                                    🤔 "{msg.ambiguousRacesCTA.user_typed}" — מצאתי {msg.ambiguousRacesCTA.options.length} מרוצים תואמים. בחר:
                                  </div>
                                  <div className="space-y-2">
                                    {msg.ambiguousRacesCTA.options.map((opt) => (
                                      <button
                                        key={opt.id}
                                        onClick={() => createPlan('current', opt.sport_type || 'running')}
                                        disabled={isCreatingPlan}
                                        className="w-full py-2 px-3 rounded-lg text-white text-sm text-right transition-all bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 disabled:opacity-50"
                                      >
                                        <div className="font-semibold">✨ {opt.race_name}</div>
                                        <div className="text-xs text-white/80 mt-0.5">{opt.race_date}{opt.distance ? ` · ${opt.distance}` : ''}</div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {msg.missingRaceCTA && msg.missingRaceCTA.race_name && (
                                <div className="mt-3 pt-3 border-t border-white/10">
                                  <div className="text-xs text-gray-400 mb-2">
                                    🆕 מרוץ חדש: <span className="text-purple-300 font-semibold">{msg.missingRaceCTA.race_name}</span> · {msg.missingRaceCTA.race_date}
                                  </div>
                                  <button
                                    onClick={() => {
                                      setRaceForm({
                                        race_name: msg.missingRaceCTA.race_name,
                                        race_date: msg.missingRaceCTA.race_date,
                                        distance: '',
                                        target_time: '',
                                        is_goal_race: false,
                                        sport_type: 'running',
                                      });
                                      setEditingRace(null);
                                      setShowAddRace(true);
                                    }}
                                    className="w-full py-2 rounded-lg text-white text-sm font-medium transition-all bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400"
                                  >
                                    ✨ הוסף מרוץ חדש
                                  </button>
                                </div>
                              )}
                              {msg.planCTA && msg.planCTA.type === 'race' && (
                                <div className="mt-3 pt-3 border-t border-white/10">
                                  <div className="text-xs text-gray-400 mb-2">
                                    {msg.planCTA.category === 'taper' ? '🏁 שבוע מרוץ' : '🏁 מרוץ מתקרב'} • {msg.planCTA.race_name} • {msg.planCTA.race_date} (בעוד {msg.planCTA.days_until} ימים)
                                  </div>
                                  <button
                                    onClick={() => createPlan(msg.planCTA.category === 'taper' ? 'current' : 'next', msg.planCTA.sport_type || 'running')}
                                    disabled={isCreatingPlan}
                                    className="w-full py-2 rounded-lg text-white text-sm font-medium transition-all bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 disabled:opacity-50"
                                  >
                                    {isCreatingPlan ? '⏳ יוצר...' : `✨ צור תכנית ל-${msg.planCTA.race_name}`}
                                  </button>
                                </div>
                              )}
                              {msg.planCTA && msg.planCTA.type === 'generic' && (
                                <div className="mt-3 pt-3 border-t border-white/10">
                                  <div className="text-xs text-gray-400 mb-2">
                                    🎯 תכנית כללית • מרחק יעד: <span className="text-cyan-300">{msg.planCTA.goal_distance}</span>{msg.planCTA.goal_time ? <> • זמן יעד: <span className="text-cyan-300">{msg.planCTA.goal_time}</span></> : null}
                                  </div>
                                  <button
                                    onClick={() => createPlan('next', msg.planCTA.sport_type || 'running')}
                                    disabled={isCreatingPlan}
                                    className="w-full py-2 rounded-lg text-white text-sm font-medium transition-all bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-500 hover:to-blue-400 disabled:opacity-50"
                                  >
                                    {isCreatingPlan ? '⏳ יוצר...' : '✨ צור תוכנית כללית לפי מרחק יעד'}
                                  </button>
                                </div>
                              )}
                              {planInfo && !msg.planCTA && (
                                <div className="mt-3 pt-3 border-t border-white/10">
                                  <button
                                    onClick={() => saveCoachChatPlan(msg.text)}
                                    disabled={isSavingPlan}
                                    className={`w-full py-2 rounded-lg text-white text-sm font-medium transition-all ${
                                      planInfo.sportType === 'cycling'
                                        ? 'bg-orange-600 hover:bg-orange-500'
                                        : 'bg-green-600 hover:bg-green-500'
                                    } disabled:opacity-50`}
                                  >
                                    {isSavingPlan ? '⏳ שומר...' : `💾 שמור כתכנית ${planInfo.sportType === 'cycling' ? 'רכיבה' : 'ריצה'} של ${athlete.name} (שבוע ${planInfo.weekStart})`}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                        {isAiThinking && <div className="flex justify-start"><div className="bg-[#0f172a] border border-white/10 p-3 rounded-xl"><div className="flex items-center gap-2"><span className="text-sm">🤖</span><span className="text-gray-500 text-xs">חושב...</span></div><div className="flex items-center gap-2 mt-2"><div className="animate-pulse text-purple-400">●</div><div className="animate-pulse text-purple-400">●</div><div className="animate-pulse text-purple-400">●</div></div></div></div>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input type="text" value={aiChatInput} onChange={(e) => setAiChatInput(e.target.value)} onKeyPress={handleAiChatKeyPress} placeholder={`שאל על ${athlete.name}...`} className="flex-1 p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm focus:border-purple-500 outline-none" disabled={isAiThinking} />
                    <button onClick={sendAiChat} disabled={isAiThinking || !aiChatInput.trim()} className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium hover:opacity-90 disabled:opacity-50">{isAiThinking ? '⏳' : '🚀'}</button>
                  </div>
                </div>
              )}

              {activeTab === 'messages' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5">
                    <p className="text-white font-medium mb-3">📤 שלח הודעה</p>
                    <textarea className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm resize-none mb-3" rows={3} placeholder="כתוב..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} />
                    <button onClick={sendMessage} disabled={isSendingMessage || !newMessage.trim()} className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium hover:opacity-90 disabled:opacity-50">{isSendingMessage ? '⏳' : '📤'} שלח</button>
                  </div>
                  <div className="p-4 rounded-xl bg-[#1e293b] border border-white/5">
                    <p className="text-gray-400 text-xs mb-3">📨 הודעות ({messages.length})</p>
                    {messages.length === 0 ? <p className="text-gray-500 text-center py-4">אין הודעות</p> : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {messages.map((msg) => <div key={msg.id} className="p-3 rounded-lg bg-[#0f172a] border border-white/5"><p className="text-gray-500 text-xs mb-1">{new Date(msg.created_at).toLocaleDateString('he-IL')}</p><p className="text-white text-sm">{msg.text}</p></div>)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      
      {showAddRace && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#1e293b] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6"><h3 className="text-xl font-bold text-white">{editingRace ? '✏️ עריכת תחרות' : '➕ הוספת תחרות'}</h3><button onClick={() => { setShowAddRace(false); setEditingRace(null); }} className="text-gray-400 hover:text-white text-xl">✕</button></div>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-xs mb-2">סוג ספורט *</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRaceForm({...raceForm, sport_type: 'running'})} className={`flex-1 py-2 px-4 rounded-xl border flex items-center justify-center gap-2 transition ${raceForm.sport_type === 'running' ? 'bg-blue-600 border-blue-500 text-white' : 'border-white/10 text-gray-400 hover:border-blue-500/50'}`}>
                    <span>🏃</span> ריצה
                  </button>
                  {CYCLING_ENABLED && (
                    <button type="button" onClick={() => setRaceForm({...raceForm, sport_type: 'cycling'})} className={`flex-1 py-2 px-4 rounded-xl border flex items-center justify-center gap-2 transition ${raceForm.sport_type === 'cycling' ? 'bg-orange-600 border-orange-500 text-white' : 'border-white/10 text-gray-400 hover:border-orange-500/50'}`}>
                      <span>🚴</span> רכיבה
                    </button>
                  )}
                </div>
              </div>
              <div><label className="block text-gray-400 text-xs mb-1">שם התחרות *</label><input type="text" value={raceForm.race_name} onChange={(e) => setRaceForm({...raceForm, race_name: e.target.value})} className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-gray-400 text-xs mb-1">תאריך *</label><input type="date" value={raceForm.race_date} onChange={(e) => setRaceForm({...raceForm, race_date: e.target.value})} className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">{raceForm.sport_type === 'cycling' ? 'סוג/מרחק *' : 'מרחק *'}</label>
                  {raceForm.sport_type === 'cycling' ? (
                    <select value={raceForm.distance} onChange={(e) => setRaceForm({...raceForm, distance: e.target.value})} className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm">
                      <option value="">בחר...</option>
                      <option value="50K">50 ק"מ</option>
                      <option value="70K">70 ק"מ</option>
                      <option value="100K">100 ק"מ</option>
                      <option value="150K">150 ק"מ</option>
                      <option value="גראן פונדו">גראן פונדו</option>
                      <option value="טריאתלון">טריאתלון</option>
                      <option value="MTB">MTB</option>
                      <option value="אחר">אחר</option>
                    </select>
                  ) : (
                    <select value={raceForm.distance} onChange={(e) => setRaceForm({...raceForm, distance: e.target.value})} className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm">
                      <option value="">בחר...</option>
                      <option value="5K">5K</option>
                      <option value="10K">10K</option>
                      <option value="15K">15K</option>
                      <option value="חצי מרתון">חצי מרתון</option>
                      <option value="מרתון">מרתון</option>
                    </select>
                  )}
                </div>
              </div>
              <div><label className="block text-gray-400 text-xs mb-1">זמן יעד {raceForm.sport_type !== 'cycling' ? '*' : '(אופציונלי לרכיבה)'}</label><input type="text" value={raceForm.target_time} onChange={(e) => setRaceForm({...raceForm, target_time: e.target.value})} placeholder="1:55:00" className="w-full p-3 rounded-xl bg-[#0f172a] border border-white/10 text-white text-sm" /></div>
              {/* Race type chosen via modal after clicking save — see RaceTypePickerModal */}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={saveRace} disabled={isSavingRace || !raceForm.race_name || !raceForm.race_date || !raceForm.distance} className={`flex-1 py-3 rounded-xl text-white font-medium hover:opacity-90 disabled:opacity-50 ${raceForm.sport_type === 'cycling' ? 'bg-gradient-to-r from-orange-600 to-yellow-500' : 'bg-gradient-to-r from-purple-600 to-pink-500'}`}>{isSavingRace ? '⏳' : editingRace ? '💾 שמור' : '➕ הוסף'}</button>
              <button onClick={() => { setShowAddRace(false); setEditingRace(null); }} className="px-6 py-3 rounded-xl bg-gray-700 text-white hover:bg-gray-600">ביטול</button>
            </div>
          </div>
        </div>
      )}

      {raceGuardrail && (
        <GoalGuardrailModal
          result={raceGuardrail.result}
          onAdoptSuggestion={() => {
            const suggested = raceGuardrail.result.suggestedDistance;
            setRaceForm((p) => ({ ...p, distance: suggested, target_time: '' }));
            setRaceGuardrail(null);
          }}
          onContinueAnyway={async () => {
            setRaceGuardrail(null);
            // Guardrail only fires on goal-race choice → pass is_goal_race=true.
            await persistRace(true, true);
          }}
          onClose={() => setRaceGuardrail(null)}
        />
      )}

      {coachRaceTypePicker && (
        <RaceTypePickerModal
          raceName={raceForm.race_name}
          raceDate={raceForm.race_date}
          onSelect={(isGoalRace) => finalizeCoachRaceAfterTypePick(isGoalRace)}
        />
      )}

      <LoadBalanceModal
        open={loadBalanceModal.open}
        weeklyKm={loadBalanceModal.weeklyKm}
        trainingDays={loadBalanceModal.trainingDays}
        requiredDays={loadBalanceModal.requiredDays}
        onFix={() => setLoadBalanceModal({ open: false })}
      />
    </div>
  );
}

export default CoachDashboard;
